import { useEffect, useState, useMemo, useCallback } from "react";
import { photoReduite } from "@/lib/utils/photo";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/firebase/api-client";
import { photoEmballage } from "@/lib/utils/emballages";
import { Order } from "@/types";
import { OrderStatus, UserRole, BasketStatus } from "@/lib/constants";
import {
  Package,
  XCircle,
  Eye,
  Filter,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Clock,
  Ban,
  Truck,
  TrendingDown,
  Receipt,
  Coins,
  FileSpreadsheet,
  CalendarDays,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import Loading from "@/components/shared/Loading";
import RetraitsCommande from "@/components/RetraitsCommande";
import { ToastContainer } from "@/components/shared/Toast";
import { useToast } from "@/lib/hooks/useToast";
import { useAuth } from "@/lib/hooks/useAuth";
import {
  useOrders as useOrdersQuery,
  useStores,
  useOrderStats,
} from "@/lib/hooks/queries";
import { getDocuments } from "@/lib/firebase/firestore";
import { COLLECTIONS } from "@/lib/constants";
import { formatGlovoCancelReason } from "@/lib/utils/format";
import { telechargerFacture, type DonneesFacture } from "@/lib/utils/facture-pdf";

/**
 * Fuseau fixe pour afficher l'heure du Maroc (19/10/2026). Les navigateurs
 * dont la base de fuseaux est anterieure au retour du Maroc a GMT
 * (20/09/2026) avancent « Africa/Casablanca » d'une heure. « UTC » tant que
 * le Maroc est a GMT ; « Etc/GMT-1 » s'il repasse a UTC+1.
 * Meme regle que functions/src/utils/heure-maroc.ts.
 */
const FUSEAU_MAROC = "UTC";

// Les raccourcis, plus une journee precise ecrite "jour:2026-08-27".
type DateFilter =
  | "today"
  | "yesterday"
  | "7days"
  | "15days"
  | "30days"
  | "all"
  | `jour:${string}`
  // « Du … au … », les deux jours inclus : "plage:2026-09-01_2026-09-16".
  | `plage:${string}`;

/** Le libelle affiche partout : sur le bouton et sur la carte Commandes. */
function libellePeriode(f: string): string {
  if (f.startsWith("plage:")) {
    const [a, b] = f.slice(6).split("_");
    const d1 = new Date(`${a}T12:00:00`);
    const d2 = new Date(`${b}T12:00:00`);
    const memeAnnee = d1.getFullYear() === d2.getFullYear();
    const court = (d: Date, avecAnnee: boolean) =>
      d.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        ...(avecAnnee ? { year: "numeric" } : {}),
      });
    return `Du ${court(d1, !memeAnnee)} au ${court(d2, true)}`;
  }
  if (f.startsWith("jour:")) {
    // Midi, pour ne pas basculer d'un jour a cause du fuseau.
    const d = new Date(`${f.slice(5)}T12:00:00`);
    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  if (f === "today") return "Aujourd'hui";
  if (f === "yesterday") return "Hier";
  if (f === "7days") return "7 derniers jours";
  if (f === "15days") return "15 derniers jours";
  if (f === "30days") return "30 derniers jours";
  return "Toutes les périodes";
}

/**
 * LES TOTAUX D'UNE COMMANDE, SOUS SES ARTICLES.
 *
 * Prix de vente : `price` est en centimes, a l'unite.
 * Prix d'achat  : `prixAchatUnitaire` est en dirhams, a l'unite.
 * Chaque ligne compte pour sa quantite. Un article sans prix d'achat n'est
 * pas compte a zero en silence : le total le dit.
 */
/**
 * Le prix A L'UNITE d'un article, en centimes (10/10/2026). Les commandes du
 * SITE enregistrees avant le 10/10 portent le prix de la LIGNE (quantite
 * comprise) : source WEBSITE sans champ `prixLigne`. Meme regle que
 * functions/src/utils/prix-article.ts.
 */
function prixUnitaire(it: any): number {
  const prix = Number(it?.price) || 0;
  const quantite = Math.max(1, Number(it?.quantity) || 1);
  return it?.source === "WEBSITE" && it?.prixLigne == null
    ? Math.round(prix / quantite)
    : prix;
}

function totauxArticles(items: any[]) {
  let venteCentimes = 0;
  let achatCentimes = 0;
  let sansAchat = 0;
  for (const it of items || []) {
    // Retire par Glovo pendant la preparation : ni vendu, ni achete.
    if (it.retireParGlovo === true) continue;
    // Quantite reduite par Glovo : on compte ce qui a ete livre.
    const commandee = Math.max(1, Number(it.quantity) || 1);
    const glovo = Number(it.quantiteGlovo);
    const q = glovo > 0 && glovo < commandee ? glovo : commandee;
    venteCentimes += prixUnitaire(it) * q;
    const a = Number(it.prixAchatUnitaire);
    if (a > 0) achatCentimes += Math.round(a * 100) * q;
    else sansAchat++;
  }
  return { venteCentimes, achatCentimes, sansAchat, ecartCentimes: venteCentimes - achatCentimes };
}
const enDH = (centimes: number) =>
  `${(centimes / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;

function TotauxArticles({ items }: { items: any[] }) {
  const t = totauxArticles(items);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
      <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
        <div className="text-[11px] font-medium text-gray-500">Total coût d&apos;achat</div>
        <div className="font-bold text-gray-900 tabular-nums">{enDH(t.achatCentimes)}</div>
        {t.sansAchat > 0 && (
          <div className="text-[11px] text-amber-700">
            {t.sansAchat} article{t.sansAchat > 1 ? "s" : ""} sans prix d&apos;achat
          </div>
        )}
      </div>
      <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
        <div className="text-[11px] font-medium text-gray-500">Total prix de vente</div>
        <div className="font-bold text-gray-900 tabular-nums">{enDH(t.venteCentimes)}</div>
      </div>
      <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
        <div className="text-[11px] font-medium text-gray-500">Différence vente − achat</div>
        <div
          className={`font-bold tabular-nums ${
            t.ecartCentimes >= 0 ? "text-emerald-700" : "text-red-700"
          }`}
        >
          {enDH(t.ecartCentimes)}
        </div>
        <div className="text-[11px] text-gray-400">avant commission et emballage</div>
      </div>
    </div>
  );
}

/**
 * L'ETAT DE LA LIVRAISON D'UNE COMMANDE DU SITE.
 *
 * Le champ « livraison » est ecrit par l'application livreurs, jamais par
 * nous : on le lit, on l'affiche. Il ne contient ni le code de retrait ni le
 * telephone du livreur — ceux-la sont dans le tableau de bord livreurs.
 */
type LivraisonCommande = {
  statut?: string;
  libelle?: string;
  livreurPrenom?: string;
  fraisAFixer?: boolean;
};
const COULEUR_LIVRAISON: Record<string, string> = {
  brouillon: "bg-gray-100 text-gray-700",
  en_attente: "bg-amber-100 text-amber-800",
  proposee: "bg-amber-100 text-amber-800",
  acceptee: "bg-emerald-600 text-white",
  recuperee: "bg-blue-100 text-blue-800",
  livree: "bg-blue-100 text-blue-800",
  annulee: "bg-red-100 text-red-700",
};
function BadgeLivraison({ l }: { l?: LivraisonCommande | null }) {
  if (!l || !l.statut) return null;
  return (
    <div
      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[11px] font-semibold rounded-full ${
        COULEUR_LIVRAISON[l.statut] || "bg-gray-100 text-gray-700"
      }`}
    >
      🛵 {l.libelle || l.statut}
      {l.livreurPrenom ? ` : ${l.livreurPrenom}` : ""}
      {l.fraisAFixer ? " · frais à fixer" : ""}
    </div>
  );
}

/** Les jours d'un mois, precedes des cases vides pour tomber sur le bon
 *  jour de semaine. La semaine commence le lundi. */
function grilleDuMois(annee: number, mois: number): (number | null)[] {
  const premier = new Date(annee, mois, 1);
  const decalage = (premier.getDay() + 6) % 7; // dimanche=0 -> 6
  const nbJours = new Date(annee, mois + 1, 0).getDate();
  return [
    ...Array(decalage).fill(null),
    ...Array.from({ length: nbJours }, (_, i) => i + 1),
  ];
}

function shouldShowOrderCancellation(order: Order): boolean {
  if (order.status === OrderStatus.CANCELLED || order.status === OrderStatus.GLOVO_CANCELLED)
    return true;
  if (order.cancellationReason?.trim()) return true;
  const ca = (order as any).cancelledAt;
  return ca != null && ca !== "";
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  GLOVO_ACCEPTED: "bg-blue-100 text-blue-800",
  IN_PREPARATION: "bg-purple-100 text-purple-800",
  GLOVO_READY: "bg-green-100 text-green-800",
  GLOVO_DELIVERED: "bg-blue-100 text-blue-800",
  CANCELLED: "bg-red-100 text-red-800",
  GLOVO_CANCELLED: "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: "En attente",
  GLOVO_ACCEPTED: "Acceptée",
  IN_PREPARATION: "En préparation",
  GLOVO_READY: "Prête",
  GLOVO_DELIVERED: "Livrée",
  CANCELLED: "Annulée",
  GLOVO_CANCELLED: "Annulée (Glovo)",
};

const PHONE_PLACEHOLDER = "+212600000000";

/** Un numero est considere absent s'il vaut N/A ou le faux numero du webhook. */
function hasRealPhone(phone?: string | null): boolean {
  return !!phone && phone !== "N/A" && phone !== PHONE_PLACEHOLDER;
}

/** Objectif de saisie : 5 minutes ou moins apres l'arrivee de la commande. */
const DELAI_CIBLE_MINUTES = 5;

/**
 * Pastille du delai de saisie : verte si l'objectif est tenu, rouge sinon.
 * Le delai est calcule et stocke par le serveur au moment de l'enregistrement.
 */
function DelaiSaisie({ minutes }: { minutes?: number | null }) {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return null;

  const dansLesTemps = minutes <= DELAI_CIBLE_MINUTES;
  const libelle = minutes < 60 ? minutes + " min" : Math.floor(minutes / 60) + " h " + (minutes % 60) + " min";

  return (
    <span
      title={
        dansLesTemps
          ? "Numero saisi dans les " + DELAI_CIBLE_MINUTES + " minutes"
          : "Objectif depasse : " + DELAI_CIBLE_MINUTES + " minutes maximum"
      }
      className={
        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums " +
        (dansLesTemps
          ? "bg-green-100 text-green-800 border border-green-300"
          : "bg-red-100 text-red-800 border border-red-300")
      }
    >
      {libelle}
    </span>
  );
}

/**
 * Cellule "telephone client" utilisable dans la liste ET dans le detail.
 * Chaque ligne gere son propre etat : deux lignes peuvent etre editees
 * l'une apres l'autre sans interference.
 */
function CustomerPhoneCell({
  order,
  canEdit,
  onSaved,
  onError,
}: {
  order: Order;
  canEdit: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const phone = order.customerPhone;
  const renseigne = hasRealPhone(phone);

  async function save() {
    if (!value.trim() || saving) return;
    try {
      setSaving(true);
      await api.orders.updatePhone(order.id, value);
      setEditing(false);
      onSaved();
    } catch (error: any) {
      onError(error?.message || "Numero invalide");
    } finally {
      setSaving(false);
    }
  }

  function ouvrir(e: { stopPropagation: () => void }) {
    e.stopPropagation();
    setValue(renseigne ? phone || "" : "");
    setEditing(true);
  }

  if (editing) {
    return (
      <div
        className="mt-1 flex flex-col gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="tel"
          inputMode="tel"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder="06 12 34 56 78  ou  +33 6 12 34 56 78"
          className="w-40 text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || !value.trim()}
            className="text-xs font-medium px-2 py-0.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "..." : "Enregistrer"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  if (renseigne) {
    return (
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-600">{phone}</span>
        <DelaiSaisie minutes={(order as any).customerPhoneDelayMinutes} />
        {canEdit && (
          <button
            type="button"
            onClick={ouvrir}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            Modifier
          </button>
        )}
      </div>
    );
  }

  // Celui qui ne peut pas modifier le numero n'a rien a faire de savoir
  // qu'il manque : la mention ne lui ouvrait aucune action, et elle occupait
  // une ligne sur CHAQUE commande. Un ecran ou tout est signale ne signale
  // plus rien.
  //
  // L'admin, lui, garde son bouton « + Ajouter le numero » juste en dessous :
  // chez lui l'information sert, puisqu'il peut agir.
  if (!canEdit) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={ouvrir}
      className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-1.5 py-0.5 hover:bg-amber-100"
    >
      + Ajouter le numero
    </button>
  );
}

/**
 * TES TROIS SOURCES DE REVENU, lisibles d'un coup d'oeil.
 *
 * Avant, seul KOOUL avait sa couleur : une commande du site s'affichait
 * « WEBSITE » en bleu, exactement comme une commande Glovo. Impossible de
 * les distinguer dans une liste de soixante lignes.
 *
 * Les couleurs de Glovo et de Kooul ne bougent pas — on ne rebat pas les
 * reperes de quelqu'un qui regarde cet ecran tous les jours.
 */
function nomSource(source?: string): string {
  if (source === "KOOUL") return "KOOUL";
  if (source === "WEBSITE") return "SITE";
  return source || "GLOVO";
}

function couleurSource(source?: string): string {
  if (source === "KOOUL") return "bg-orange-100 text-orange-800";
  if (source === "WEBSITE") return "bg-emerald-100 text-emerald-800";
  return "bg-blue-100 text-blue-800";
}

/**
 * Les donnees de la facture client d'une commande (10/10/2026).
 *
 * Prix de VENTE seulement — jamais d'achat ni de fournisseur. Le total est
 * celui que le client a paye (totalAmount). Si articles + livraison ne
 * tombent pas juste (remise, frais, article retire), l'ecart apparait sur
 * une ligne a part : la facture reste exacte et lisible.
 *
 * Le prix de chaque article passe par prixUnitaire() : les anciennes
 * commandes du site portaient le prix de la ligne.
 */
function donneesFacture(order: any): DonneesFacture {
  const items: any[] = (Array.isArray(order?.items) ? order.items : []).filter(
    (i: any) => !i.retireParGlovo,
  );
  const total = Math.round(Number(order?.totalAmount) || 0);
  const livraison = Math.round(Number(order?.deliveryFee) || 0);

  const lignes = items.map((i) => ({
    designation: String(i.productName || i.name || "Article"),
    quantite: Number(i.quantity) || 1,
    prixUnitaireCentimes: Math.round(prixUnitaire(i)),
  }));
  const sousTotal = lignes.reduce((s, l) => s + l.prixUnitaireCentimes * l.quantite, 0);
  const totalFinal = total > 0 ? total : sousTotal + livraison;

  const brute = order?.orderTime || order?.createdAt;
  let date = new Date();
  try {
    const d = brute?.toDate ? brute.toDate() : brute?._seconds ? new Date(brute._seconds * 1000) : new Date(brute);
    if (!isNaN(d.getTime())) date = d;
  } catch {
    /* date du jour */
  }

  const origine =
    order?.source === "WEBSITE"
      ? "Commande passée sur naturabeldi.ma"
      : order?.source === "KOOUL"
        ? "Commande passée via Kooul"
        : "Commande passée via Glovo";
  const paiement =
    order?.source === "WEBSITE"
      ? order?.moyenPaiement && LIBELLES_PAIEMENT[order.moyenPaiement]
        ? `Paiement : ${LIBELLES_PAIEMENT[order.moyenPaiement].toLowerCase()}`
        : order?.paymentMethod === "DELAYED"
          ? "Paiement : payé en ligne"
          : "Paiement : espèces à la livraison"
      : undefined;

  return {
    numero: String(order?.orderCode || order?.externalId || order?.id || ""),
    date,
    magasin: order?.store?.name || undefined,
    origine,
    paiement,
    client: {
      nom: order?.customerName || undefined,
      telephone: order?.customerPhone || undefined,
      email: order?.customerEmail || undefined,
      adresse: order?.deliveryAddress || undefined,
    },
    lignes,
    livraisonCentimes: livraison,
    ajustementCentimes: totalFinal - sousTotal - livraison,
    totalCentimes: totalFinal,
  };
}

const LIBELLES_PAIEMENT: Record<string, string> = {
  ESPECES: "Espèces",
  VIREMENT: "Virement",
  CARTE: "Carte bancaire",
};

/**
 * Le paiement d'une commande du site, d'un coup d'oeil dans la liste
 * (10/10/2026) : « en attente » tant que le support ne l'a pas encaisse.
 * Rien pour Glovo et Kooul : c'est la plateforme qui encaisse.
 */
function BadgePaiement({ order }: { order: any }) {
  if (order?.source !== "WEBSITE") return null;
  if (["CANCELLED", "GLOVO_CANCELLED"].includes(String(order?.status))) return null;
  const moyen = LIBELLES_PAIEMENT[order?.moyenPaiement] || "";
  return order?.paiementEncaisse === true ? (
    <span className="inline-block mt-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-green-100 text-green-800">
      💰 Encaissé{moyen ? ` · ${moyen}` : ""}
    </span>
  ) : (
    <span className="inline-block mt-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800">
      ⏳ Paiement en attente
    </span>
  );
}

/** Le bouton d'encaissement, dans le detail d'une commande du site. */
function EncaissementSite({
  order,
  onSaved,
  onError,
}: {
  order: any;
  onSaved: (maj: { paiementEncaisse: boolean; moyenPaiement: string | null; caisseEncaissement?: any }) => void;
  onError: (message: string) => void;
}) {
  const [moyen, setMoyen] = useState<string>(order?.moyenPaiement || "");
  const [enCours, setEnCours] = useState(false);
  // LA CAISSE QUI ENCAISSE ET PAIE LE LIVREUR (25/10/2026).
  const [caisseId, setCaisseId] = useState<string>(order?.caisseEncaissement?.responsableId || "");
  const [caisses, setCaisses] = useState<Array<{ uid: string; nom: string }>>([]);
  useEffect(() => {
    if (!order?.storeId) return;
    api.orders
      .equipe(order.storeId)
      .then((r: any) =>
        setCaisses((Array.isArray(r?.equipe) ? r.equipe : []).map((e: any) => ({ uid: String(e.uid), nom: String(e.nom || "") }))),
      )
      .catch(() => setCaisses([]));
  }, [order?.storeId]);
  useEffect(() => {
    setCaisseId(order?.caisseEncaissement?.responsableId || "");
  }, [order?.id, order?.caisseEncaissement?.responsableId]);

  useEffect(() => {
    setMoyen(order?.moyenPaiement || "");
  }, [order?.id, order?.moyenPaiement]);

  if (["CANCELLED", "GLOVO_CANCELLED"].includes(String(order?.status))) return null;
  const encaisse = order?.paiementEncaisse === true;

  const envoyer = async (valeur: boolean) => {
    if (valeur && !moyen) {
      onError("Choisis le moyen de paiement.");
      return;
    }
    if (valeur && moyen === "ESPECES" && !caisseId) {
      onError("Espèces : choisis la caisse qui reçoit l'argent.");
      return;
    }
    setEnCours(true);
    try {
      const r = await api.orders.paiement(order.id, {
        encaisse: valeur,
        ...(moyen ? { moyenPaiement: moyen as "ESPECES" | "VIREMENT" | "CARTE" } : {}),
        ...(valeur && moyen === "ESPECES" ? { caisseId } : {}),
      });
      onSaved({
        paiementEncaisse: r.paiementEncaisse,
        moyenPaiement: r.moyenPaiement,
        caisseEncaissement: r.caisseEncaissement ?? null,
      });
    } catch (e: any) {
      onError(e?.message || "Enregistrement impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div
      className={`mt-3 rounded-md border p-2 bg-white ${
        encaisse ? "border-green-300" : "border-amber-300"
      }`}
    >
      <div className="text-xs font-semibold mb-1">
        Paiement :{" "}
        {encaisse ? (
          <span className="text-green-700">
            encaissé{LIBELLES_PAIEMENT[order?.moyenPaiement] ? ` (${LIBELLES_PAIEMENT[order.moyenPaiement]})` : ""}
            {order?.caisseEncaissement?.responsableId
              ? ` · caisse de ${caisses.find((c) => c.uid === order.caisseEncaissement.responsableId)?.nom || "?"}`
              : ""}
          </span>
        ) : (
          <span className="text-amber-700">en attente</span>
        )}
      </div>
      {encaisse ? (
        <button
          disabled={enCours}
          onClick={() => envoyer(false)}
          className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {enCours ? "…" : "Erreur : remettre en attente"}
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={moyen}
            onChange={(e) => setMoyen(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">Moyen de paiement…</option>
            <option value="ESPECES">Espèces</option>
            <option value="VIREMENT">Virement</option>
            <option value="CARTE">Carte bancaire</option>
          </select>
          {moyen === "ESPECES" && (
          <select
            value={caisseId}
            onChange={(e) => setCaisseId(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
            title="Les espèces du client entrent dans cette caisse."
          >
            <option value="">Espèces reçues par…</option>
            {caisses.map((c) => (
              <option key={c.uid} value={c.uid}>
                🧾 Caisse de {c.nom}
              </option>
            ))}
          </select>
          )}
          <button
            disabled={enCours}
            onClick={() => envoyer(true)}
            className="text-xs px-3 py-1 rounded bg-green-600 text-white font-medium disabled:opacity-50"
          >
            {enCours ? "…" : "💰 Paiement encaissé"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Frais de livraison et moyen de paiement d'une commande du SITE, saisis
 * par le support en attendant l'application des livreurs (10/10/2026).
 * Le total de la commande est recalcule par le serveur.
 */
function LivraisonPaiementSite({
  order,
  peutModifier,
  onSaved,
  onError,
}: {
  order: any;
  peutModifier: boolean;
  onSaved: (maj: {
    deliveryFee: number;
    totalAmount: number;
    moyenPaiement: string;
    livreurPaye?: any;
    livraisonOfferte?: boolean;
  }) => void;
  onError: (message: string) => void;
}) {
  const [offerte, setOfferte] = useState<boolean>(order?.livraisonOfferte === true);
  const [frais, setFrais] = useState<string>(
    order?.deliveryFee != null ? String(Number(order.deliveryFee) / 100) : "",
  );
  const [moyen, setMoyen] = useState<string>(order?.moyenPaiement || "");
  const [enCours, setEnCours] = useState(false);
  // LA CAISSE QUI PAIE LE LIVREUR (25/10/2026) : la cliente peut payer par
  // carte, le livreur est paye en especes par un responsable.
  const [caisseLivreur, setCaisseLivreur] = useState<string>(order?.livreurPaye?.responsableId || "");
  const [caisses, setCaisses] = useState<Array<{ uid: string; nom: string }>>([]);
  useEffect(() => {
    if (!order?.storeId) return;
    api.orders
      .equipe(order.storeId)
      .then((r: any) =>
        setCaisses((Array.isArray(r?.equipe) ? r.equipe : []).map((e: any) => ({ uid: String(e.uid), nom: String(e.nom || "") }))),
      )
      .catch(() => setCaisses([]));
  }, [order?.storeId]);

  useEffect(() => {
    setFrais(order?.deliveryFee != null ? String(Number(order.deliveryFee) / 100) : "");
    setMoyen(order?.moyenPaiement || "");
    setCaisseLivreur(order?.livreurPaye?.responsableId || "");
    setOfferte(order?.livraisonOfferte === true);
  }, [order?.id]);

  const total = (Number(order?.totalAmount) || 0) / 100;
  const livraison = (Number(order?.deliveryFee) || 0) / 100;
  const offerteEnregistree = order?.livraisonOfferte === true;

  if (!peutModifier) {
    return (
      <p className="text-xs text-gray-700 mt-2">
        Livraison : <b>{livraison.toFixed(2)} DH</b>
        {offerteEnregistree ? " (offerte, à notre charge)" : ""} · Paiement :{" "}
        <b>{LIBELLES_PAIEMENT[order?.moyenPaiement] || "non renseigné"}</b>
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-emerald-300 bg-white p-2">
      <div className="text-xs font-semibold text-emerald-900 mb-1">
        Livraison et paiement
        {order?.fraisLivraisonAFixer && (
          <span className="ml-2 text-red-600">⚠ frais à fixer</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">Frais (DH)</label>
        <input
          type="number"
          min={0}
          max={500}
          step="0.5"
          value={frais}
          onChange={(e) => setFrais(e.target.value)}
          className="w-20 text-xs border border-gray-300 rounded px-2 py-1"
        />
        <select
          value={moyen}
          onChange={(e) => setMoyen(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          <option value="">Moyen de paiement…</option>
          <option value="ESPECES">Espèces</option>
          <option value="VIREMENT">Virement</option>
          <option value="CARTE">Carte bancaire</option>
        </select>
        <select
          value={caisseLivreur}
          onChange={(e) => setCaisseLivreur(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
          title="Les frais de livraison sortent de cette caisse."
        >
          <option value="">Livreur payé par…</option>
          {caisses.map((c) => (
            <option key={c.uid} value={c.uid}>
              🧾 Caisse de {c.nom}
            </option>
          ))}
        </select>
        <label
          className="flex items-center gap-1 text-xs text-gray-700"
          title="Le client ne paie que ses articles ; les frais du livreur sont à notre charge."
        >
          <input type="checkbox" checked={offerte} onChange={(e) => setOfferte(e.target.checked)} />
          Livraison offerte
        </label>
        <button
          disabled={enCours}
          onClick={async () => {
            const f = Number(String(frais).replace(",", "."));
            if (!Number.isFinite(f) || f < 0 || f > 500) {
              onError("Frais de livraison invalides (entre 0 et 500 DH).");
              return;
            }
            if (!moyen) {
              onError("Choisis le moyen de paiement.");
              return;
            }
            if (f > 0 && !caisseLivreur) {
              onError("Choisis la caisse qui paie le livreur.");
              return;
            }
            setEnCours(true);
            try {
              const r = await api.orders.livraison(order.id, {
                fraisLivraison: f,
                moyenPaiement: moyen as "ESPECES" | "VIREMENT" | "CARTE",
                ...(caisseLivreur ? { caisseLivreurId: caisseLivreur } : {}),
                livraisonOfferte: offerte,
              });
              onSaved({
                livraisonOfferte: r.livraisonOfferte === true,
                deliveryFee: r.deliveryFee,
                totalAmount: r.totalAmount,
                moyenPaiement: r.moyenPaiement,
                livreurPaye: (r as any).livreurPaye ?? null,
              });
            } catch (e: any) {
              onError(e?.message || "Enregistrement impossible.");
            } finally {
              setEnCours(false);
            }
          }}
          className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-medium disabled:opacity-50"
        >
          {enCours ? "…" : "Enregistrer"}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mt-1">
        {offerteEnregistree ? (
          <>
            Le client paie : <b>{total.toFixed(2)} DH</b> (articles seuls) · livraison{" "}
            {livraison.toFixed(2)} DH offerte, à notre charge.
          </>
        ) : (
          <>
            Le client paie : <b>{total.toFixed(2)} DH</b>, dont livraison {livraison.toFixed(2)} DH.
          </>
        )}
        {order?.livreurPaye?.responsableId ? (
          <span className="ml-1 font-semibold text-emerald-800">
            · Livreur payé par la caisse de{" "}
            {caisses.find((c) => c.uid === order.livreurPaye.responsableId)?.nom || "?"} (
            {(Number(order.livreurPaye.montantCentimes) / 100).toFixed(2)} DH)
          </span>
        ) : livraison > 0 ? (
          <span className="ml-1 font-semibold text-red-600">· livreur : caisse non choisie</span>
        ) : null}
      </p>
    </div>
  );
}

/**
 * RETIRER UN ARTICLE DE LA COMMANDE (02/11/2026) — sans validation, il part
 * aussitot en ventes perdues. Glovo : avant « prete » seulement. Site et
 * Kooul : le support aussi apres (correction de ce qui a ete livre). Le
 * serveur verifie les memes regles : ce bouton n'est qu'un raccourci.
 */
function RetirerArticle({
  item,
  order,
  estSupport,
  onDone,
  onError,
}: {
  item: any;
  order: any;
  estSupport: boolean;
  onDone: (message: string) => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [confirmer, setConfirmer] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const st = String(item?.retrait?.statut || "");
  if (!item?.id || item.caissePayee || item.retireParGlovo === true) return null;
  if (["RETIRE", "RETIRE_GLOVO", "A_RETIRER_TABLETTE", "EN_ATTENTE"].includes(st)) return null;
  const statut = String(order?.status || "");
  if (statut === "CANCELLED" || statut === "GLOVO_CANCELLED") return null;
  const modifiable = ["PENDING", "GLOVO_ACCEPTED", "IN_PREPARATION"].includes(statut);
  const estGlovo = !order?.source || order.source === "GLOVO";
  // Glovo deja prete ou livree : correction a la main, direction et support
  // seulement (02/11/2026). Glovo n'est pas appele.
  const correctionGlovo = !modifiable && estGlovo && estSupport;
  if (!modifiable && !estSupport) return null;

  if (!confirmer) {
    return (
      <button
        type="button"
        onClick={() => setConfirmer(true)}
        className="mt-1 block text-[11px] font-semibold text-red-600 hover:underline"
      >
        Retirer de la commande
      </button>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
      <span className="font-semibold text-red-700">
        {correctionGlovo
          ? "Glovo l'a retiré ? On le note chez nous (Glovo n'est pas prévenu)."
          : "Retirer ? Il passera en ventes perdues."}
      </span>
      <button
        type="button"
        disabled={envoi}
        onClick={async () => {
          setEnvoi(true);
          try {
            const r = await api.retraitsCommande.retirer(item.id);
            await onDone(`Article retiré — ventes perdues${r.raison ? ` — ${r.raison}` : ""}`);
          } catch (e: any) {
            onError(e?.message || "Retrait impossible.");
          } finally {
            setEnvoi(false);
            setConfirmer(false);
          }
        }}
        className="rounded bg-red-600 px-2 py-0.5 font-bold text-white disabled:opacity-50"
      >
        {envoi ? "…" : "Oui, retirer"}
      </button>
      <button type="button" onClick={() => setConfirmer(false)} className="text-gray-500 underline">
        non
      </button>
    </span>
  );
}

/**
 * LE LIVREUR EN PLUS (02/11/2026) — un article oublie, envoye avec un
 * livreur a nos frais. Le frais sort d'une caisse et se retire de la marge
 * de la commande (comptabilite).
 */
function LivreurSupplementaire({
  order,
  estAdmin,
  onDone,
  onError,
}: {
  order: any;
  estAdmin: boolean;
  onDone: (message: string) => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [montant, setMontant] = useState("");
  const [caisse, setCaisse] = useState("");
  const [motif, setMotif] = useState("");
  const [articleId, setArticleId] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [caisses, setCaisses] = useState<Array<{ uid: string; nom: string }>>([]);
  const articles: any[] = (Array.isArray(order?.items) ? order.items : []).filter(
    (i: any) => i?.id && i.retireParGlovo !== true,
  );
  useEffect(() => {
    if (!ouvert || !order?.storeId) return;
    api.orders
      .equipe(order.storeId)
      .then((r: any) => {
        const l = (Array.isArray(r?.equipe) ? r.equipe : []).map((e: any) => ({
          uid: String(e.uid),
          nom: String(e.nom || ""),
        }));
        setCaisses(l);
        // Par defaut : la caisse du responsable de la commande.
        const resp = l.find((c: any) => c.uid === String(order?.responsableId || ""));
        setCaisse((v) => v || (resp ? resp.uid : l.length === 1 ? l[0].uid : ""));
      })
      .catch(() => setCaisses([]));
  }, [ouvert, order?.storeId, order?.responsableId]);

  const lignes: any[] = Array.isArray(order?.livreursSupplementaires) ? order.livreursSupplementaires : [];
  const total = Number(order?.fraisLivreurSupplementaireCentimes) || 0;
  const t = totauxArticles(order?.items || []);

  return (
    <div className="rounded-lg border-2 border-orange-300 bg-orange-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-orange-900">🛵 Article oublié — livreur à nos frais</p>
        {!ouvert && (
          <button
            type="button"
            onClick={() => setOuvert(true)}
            className="text-xs px-3 py-1.5 rounded bg-orange-600 text-white font-semibold hover:bg-orange-700"
          >
            + Frais de livreur (article oublié)
          </button>
        )}
      </div>
      {lignes.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-700">
          {lignes.map((l: any) => (
            <li key={l.depenseId} className="flex flex-wrap items-center gap-2">
              <b>{enDH(Number(l.montantCentimes) || 0)}</b>
              <span>
                caisse {l.caisseNom || "?"} · {l.articleNom ? `oublié : ${l.articleNom}` : l.motif}
                {l.responsable ? ` · responsable : ${l.responsable}` : ""}
                {l.parNom ? ` · saisi par ${l.parNom}` : ""}
              </span>
              {estAdmin && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.orders.annulerLivreurSupplementaire(order.id, String(l.depenseId));
                      await onDone("Frais de livreur annulé (ligne écartée de la caisse).");
                    } catch (e: any) {
                      onError(e?.message || "Annulation impossible.");
                    }
                  }}
                  className="text-red-600 underline"
                >
                  annuler
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {total > 0 && (
        <p className="mt-1 text-xs">
          Marge vente − achat − livreur :{" "}
          <b className={t.ecartCentimes - total >= 0 ? "text-emerald-700" : "text-red-700"}>
            {enDH(t.ecartCentimes - total)}
          </b>
        </p>
      )}
      {ouvert && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            inputMode="decimal"
            placeholder="Frais (DH)"
            className="w-24 text-xs border border-gray-300 rounded px-2 py-1"
          />
          <select
            value={caisse}
            onChange={(e) => setCaisse(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">Payé par la caisse de…</option>
            {caisses.map((c) => (
              <option key={c.uid} value={c.uid}>
                🧾 Caisse de {c.nom}
              </option>
            ))}
          </select>
          <select
            value={articleId}
            onChange={(e) => setArticleId(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[16rem]"
          >
            <option value="">Article oublié…</option>
            {articles.map((i: any) => (
              <option key={i.id} value={i.id}>
                {Number(i.quantity) > 1 ? `${Number(i.quantity)} × ` : ""}
                {i.productName || "Article"}
              </option>
            ))}
            <option value="AUTRE">Autre (pas dans la liste)</option>
          </select>
          {articleId === "AUTRE" && (
            <input
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Lequel ? (ex. : coriandre)"
              className="flex-1 min-w-[10rem] text-xs border border-gray-300 rounded px-2 py-1"
            />
          )}
          <button
            type="button"
            disabled={envoi}
            onClick={async () => {
              const m = Number(String(montant).replace(",", "."));
              if (!(m > 0)) return onError("Écris le prix du livreur, en DH.");
              if (!caisse) return onError("Choisis la caisse qui a payé le livreur.");
              if (!articleId) return onError("Choisis l'article oublié.");
              setEnvoi(true);
              try {
                await api.orders.livreurSupplementaire(order.id, {
                  montant: m,
                  caisseId: caisse,
                  ...(articleId !== "AUTRE" ? { articleId } : {}),
                  ...(articleId === "AUTRE" && motif.trim() ? { motif: `Oublié : ${motif.trim()}` } : {}),
                });
                setMontant("");
                setMotif("");
                setArticleId("");
                setOuvert(false);
                await onDone("Frais de livreur ajouté : il sort de la caisse et se retire de la marge de l'article oublié.");
              } catch (e: any) {
                onError(e?.message || "Enregistrement impossible.");
              } finally {
                setEnvoi(false);
              }
            }}
            className="text-xs px-3 py-1 rounded bg-orange-600 text-white font-medium disabled:opacity-50"
          >
            {envoi ? "…" : "Enregistrer"}
          </button>
          <button type="button" onClick={() => setOuvert(false)} className="text-xs text-gray-500">
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * CORRIGER LE PRIX DE VENTE D'UN ARTICLE (02/11/2026) — direction et
 * support. Prix a l'unite, en DH ; l'ancien prix est garde par le serveur.
 */
function ModifierPrixVente({
  item,
  order,
  onDone,
  onError,
}: {
  item: any;
  order: any;
  onDone: (message: string) => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [valeur, setValeur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const statut = String(order?.status || "");
  if (!item?.id || item.retireParGlovo === true) return null;
  if (statut === "CANCELLED" || statut === "GLOVO_CANCELLED") return null;
  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => {
          setValeur(String((prixUnitaire(item) / 100).toFixed(2)).replace(/\.00$/, ""));
          setOuvert(true);
        }}
        className="mt-1 block text-[11px] font-semibold text-indigo-600 hover:underline"
      >
        Modifier le prix
      </button>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
      <input
        value={valeur}
        onChange={(e) => setValeur(e.target.value)}
        inputMode="decimal"
        className="w-16 rounded border border-gray-300 px-1 py-0.5 text-xs"
        autoFocus
      />
      <span className="text-gray-500">DH/u</span>
      <button
        type="button"
        disabled={envoi}
        onClick={async () => {
          const p = Number(String(valeur).replace(",", "."));
          if (!(p > 0)) {
            onError("Écris le prix de vente, à l'unité.");
            return;
          }
          setEnvoi(true);
          try {
            const r = await api.orders.prixVente(order.id, item.id, p);
            await onDone(r.inchange ? "Prix inchangé." : `Prix de vente corrigé : ${p} DH`);
            setOuvert(false);
          } catch (e: any) {
            onError(e?.message || "Correction impossible.");
          } finally {
            setEnvoi(false);
          }
        }}
        className="rounded bg-indigo-600 px-2 py-0.5 font-bold text-white disabled:opacity-50"
      >
        {envoi ? "…" : "OK"}
      </button>
      <button type="button" onClick={() => setOuvert(false)} className="text-gray-500 underline">
        annuler
      </button>
    </span>
  );
}

/**
 * LE FOURNISSEUR QUI TRAITE CET ARTICLE (11/10/2026) — celui du panier ou
 * l'article a ete range. Pour verifier l'affectation d'un coup d'oeil.
 */
function FournisseurDeLArticle({ item, order }: { item: any; order: any }) {
  const baskets: any[] = Array.isArray(order?.baskets) ? order.baskets : [];
  const b = item?.basketId ? baskets.find((x) => x.id === item.basketId) : null;
  if (!b && item?.achatCaisse) {
    // Toujours la caisse DE QUI (02/11/2026) : celle qui a paye, ou celle
    // du responsable de la commande tant que l'achat n'est pas saisi.
    const nom = String(item?.caisseNom || order?.responsableNom || "").trim();
    return (
      <span className="block text-[11px] font-semibold text-teal-700">
        → 🧾 caisse {nom || "— responsable non attribué"}
        {item?.caisseAchetee === false && nom ? (
          <span className="font-normal text-amber-700"> (achat pas encore saisi)</span>
        ) : null}
      </span>
    );
  }
  if (!b) {
    return (
      <span className="block text-[11px] font-semibold text-amber-700">
        {String(order?.status) === "PENDING"
          ? "fournisseur : attribué à la validation"
          : "fournisseur : aucun panier"}
      </span>
    );
  }
  return (
    <span
      className={`block text-[11px] font-semibold ${
        b.status === "CANCELLED" ? "text-gray-400 line-through" : "text-indigo-700"
      }`}
    >
      → {b.fournisseurName || "fournisseur inconnu"}
    </span>
  );
}

/**
 * Ajouter un article a une commande du site (11/10/2026) — direction et
 * support. Le client appelle pour un produit de plus : on l'ajoute ici, il
 * part chez son fournisseur et le total suit.
 */
function AjouterArticleSite({
  order,
  onDone,
  onError,
}: {
  order: any;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState("");
  const [resultats, setResultats] = useState<any[]>([]);
  const [cherche, setCherche] = useState(false);
  const [choisi, setChoisi] = useState<any | null>(null);
  const [quantite, setQuantite] = useState("1");
  const [prix, setPrix] = useState("");
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    setOuvert(false);
    setRecherche("");
    setResultats([]);
    setChoisi(null);
    setQuantite("1");
    setPrix("");
  }, [order?.id]);

  const statut = String(order?.status || "");
  if (["GLOVO_DELIVERED", "CANCELLED", "GLOVO_CANCELLED"].includes(statut)) {
    return null;
  }
  const source = String(order?.source || "GLOVO");
  const plateforme = source === "KOOUL" ? "Kooul" : source === "WEBSITE" ? null : "Glovo";

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="mt-3 mr-2 text-xs px-3 py-1 rounded border border-blue-400 bg-white text-blue-800 hover:bg-blue-50"
      >
        + Ajouter un article
      </button>
    );
  }

  const chercher = async () => {
    if (!recherche.trim() || !order?.storeId) return;
    setCherche(true);
    try {
      const r = await api.stores.getProducts(order.storeId, {
        search: recherche.trim(),
        limit: 10,
      });
      setResultats(r.products || []);
      if ((r.products || []).length === 0) onError("Aucun article trouvé dans ce magasin.");
    } catch (e: any) {
      onError(e?.message || "Recherche impossible.");
    } finally {
      setCherche(false);
    }
  };

  // Le prix du canal de la commande : son prix propre, sinon le prix Glovo.
  const prixCatalogue = (p: any) => {
    const propre =
      source === "WEBSITE" ? Number(p?.sitePrice) : source === "KOOUL" ? Number(p?.kooulPrice) : Number(p?.glovoPrice);
    return propre > 0 ? propre : Number(p?.glovoPrice) > 0 ? Number(p.glovoPrice) : 0;
  };

  const q = Math.max(1, Math.trunc(Number(quantite) || 1));
  const pu = Number(String(prix).replace(",", ".")) || 0;

  return (
    <div className="mt-3 rounded-md border border-blue-300 bg-white p-2 space-y-2">
      <div className="text-xs font-semibold text-blue-900">Ajouter un article</div>
      {plateforme && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {plateforme === "Glovo" ? (
            <>
              Commande Glovo : l&apos;article est ajouté automatiquement chez Glovo (le client le paie). Si Glovo
              refuse (commande déjà prête), rien n&apos;est ajouté : ajoute-le sur la tablette.
            </>
          ) : (
            <>
              Commande {plateforme} : c&apos;est {plateforme} qui encaisse. Ajoute aussi l&apos;article chez{" "}
              {plateforme}, sinon le client ne le paiera pas.
            </>
          )}
        </p>
      )}
      <div className="flex gap-2">
        <input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void chercher();
          }}
          placeholder="Nom du produit…"
          className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
        />
        <button
          onClick={() => void chercher()}
          disabled={cherche || !recherche.trim()}
          className="text-xs px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50"
        >
          {cherche ? "…" : "Chercher"}
        </button>
      </div>

      {resultats.length > 0 && (
        <div className="max-h-48 overflow-auto border rounded divide-y">
          {resultats.map((p: any) => (
            <button
              key={p.sku}
              onClick={() => {
                setChoisi(p);
                setResultats([]);
                const pc = prixCatalogue(p);
                setPrix(pc ? String(pc) : "");
              }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50"
            >
              <span className="font-medium">{p.name}</span>
              <span className="text-gray-500">
                {" "}
                — {prixCatalogue(p) ? `${prixCatalogue(p)} DH` : "prix inconnu"}
                {p.isActive === false ? " — inactif" : ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {choisi && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-800 flex-1 min-w-[160px]">
            {choisi.name}
          </span>
          <label className="text-xs text-gray-600">Qté</label>
          <input
            type="number"
            min={1}
            max={50}
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            className="w-14 text-xs border border-gray-300 rounded px-2 py-1"
          />
          <label className="text-xs text-gray-600">Prix unitaire (DH)</label>
          <input
            type="number"
            min={0}
            step="0.5"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
            className="w-20 text-xs border border-gray-300 rounded px-2 py-1"
          />
          <button
            disabled={enCours || pu <= 0}
            onClick={async () => {
              setEnCours(true);
              try {
                const envoyer = (dejaSurTablette: boolean) =>
                  api.orders.ajouterArticle(order.id, {
                    sku: choisi.sku,
                    quantite: q,
                    prixUnitaire: pu,
                    ...(dejaSurTablette ? { dejaSurTablette: true } : {}),
                  });
                let r;
                try {
                  r = await envoyer(false);
                } catch (e: any) {
                  // GLOVO A REFUSE (04/11/2026) : rien n'a ete ajoute. On ne
                  // l'ajoute chez nous que si l'article est deja sur la tablette.
                  const msg = String(e?.message || "");
                  if (!/Rien n'a été ajouté/.test(msg)) throw e;
                  const ok = window.confirm(
                    `${msg}\n\nAs-tu déjà ajouté l'article sur la tablette Glovo ?\nOK = oui, l'ajouter aussi ici · Annuler = ne rien ajouter`,
                  );
                  if (!ok) throw new Error("Rien n'a été ajouté : ajoute d'abord l'article sur la tablette Glovo.");
                  r = await envoyer(true);
                }
                onDone(
                  `${q} × ${r.nom} ajouté (${(r.prixLigne / 100).toFixed(2)} DH)` +
                    (r.surGlovo ? " — ajouté aussi sur Glovo" : "") +
                    (r.enAttenteDeValidation
                      ? " — il partira chez le fournisseur à la validation."
                      : " — envoyé chez le fournisseur."),
                );
                setChoisi(null);
                setRecherche("");
                setQuantite("1");
                setPrix("");
                setOuvert(false);
              } catch (e: any) {
                onError(e?.message || "Ajout impossible.");
              } finally {
                setEnCours(false);
              }
            }}
            className="text-xs px-3 py-1 rounded bg-blue-600 text-white font-medium disabled:opacity-50"
          >
            {enCours ? "…" : `Ajouter (${(q * pu).toFixed(2)} DH)`}
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-[11px] text-gray-500">
          Le total de la commande augmente du montant ajouté.
        </p>
        <button
          onClick={() => setOuvert(false)}
          className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

/**
 * Commande du site DEJA LIVREE hors application (10/10/2026).
 * La passe « livrée » en un geste, sans prévenir ni les fournisseurs ni le
 * client. Les paniers manquants sont créés déjà récupérés : la commande
 * rejoint l'historique de chaque fournisseur.
 */
function RegulariserLivree({
  order,
  onDone,
  onError,
}: {
  order: any;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [kraft, setKraft] = useState("0");
  const [tissu, setTissu] = useState("0");
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    setOuvert(false);
    setKraft("0");
    setTissu("0");
  }, [order?.id]);

  const statut = String(order?.status || "");
  if (
    order?.source !== "WEBSITE" ||
    ["GLOVO_DELIVERED", "CANCELLED", "GLOVO_CANCELLED"].includes(statut)
  ) {
    return null;
  }

  // 17/10/2026 : WC75539D a ete « regularisee » au lieu d'etre validee —
  // rien n'a ete prepare. Le bouton est donc gris, mis a l'ecart, et dit en
  // rouge ce qu'il ne fait PAS.
  const avertissement = (
    <p className="text-[11px] font-semibold text-red-700">
      ⚠️ Les fournisseurs ne seront PAS prévenus. Pour une commande à préparer,
      utilise « Livreur trouvé — valider la commande ».
    </p>
  );

  if (!ouvert) {
    return (
      <div className="mt-8 border-t border-dashed border-gray-300 pt-3">
        <p className="text-[11px] text-gray-400 mb-1">
          Uniquement pour une commande DÉJÀ livrée hors application :
        </p>
        <button
          onClick={() => setOuvert(true)}
          className="text-xs px-3 py-1 rounded border border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200"
        >
          Déjà livrée — régulariser
        </button>
        <div className="mt-1">{avertissement}</div>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-md border-2 border-red-300 bg-red-50 p-2">
      <div className="text-xs font-semibold text-red-900 mb-1">
        Cette commande a VRAIMENT déjà été livrée au client ?
      </div>
      <p className="text-[11px] text-gray-700 mb-1">
        Elle passe « Livrée » et rejoint l&apos;historique des fournisseurs.
        Aucun message n&apos;est envoyé, ni aux fournisseurs ni au client.
      </p>
      <div className="mb-2">{avertissement}</div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">Sacs kraft</label>
        <input
          type="number"
          min={0}
          value={kraft}
          onChange={(e) => setKraft(e.target.value)}
          className="w-16 text-xs border border-gray-300 rounded px-2 py-1"
        />
        <label className="text-xs text-gray-600">Sacs tissu</label>
        <input
          type="number"
          min={0}
          value={tissu}
          onChange={(e) => setTissu(e.target.value)}
          className="w-16 text-xs border border-gray-300 rounded px-2 py-1"
        />
        <button
          disabled={enCours}
          onClick={async () => {
            setEnCours(true);
            try {
              const r = await api.orders.regulariserLivree(order.id, {
                sacKraft: Math.max(0, Math.trunc(Number(kraft) || 0)),
                sacTissu: Math.max(0, Math.trunc(Number(tissu) || 0)),
              });
              onDone(
                "Commande passée livrée" +
                  (r.paniersCrees ? " — paniers créés dans l'historique des fournisseurs." : "."),
              );
              setOuvert(false);
            } catch (e: any) {
              onError(e?.message || "Régularisation impossible.");
            } finally {
              setEnCours(false);
            }
          }}
          className="text-xs px-3 py-1 rounded bg-gray-700 text-white font-medium disabled:opacity-50"
        >
          {enCours ? "…" : "Confirmer : déjà livrée"}
        </button>
        <button
          onClick={() => setOuvert(false)}
          className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

/**
 * ANNULER UNE COMMANDE QU'ON NE PEUT PAS SERVIR (29/10/2026) — direction et
 * support. Un motif est obligatoire (produit indisponible, hors zone…) ; il
 * est garde sur la commande avec le nom de qui a annule.
 */
const MOTIFS_ANNULATION = [
  "Produit indisponible — nous ne pouvons pas le faire",
  "Client hors zone de livraison",
  "Client injoignable",
  "Demande du client",
];

/** Meme regle que le serveur : annulable tant que pas annulee, et une
 * commande du site « livrée » seulement si le paiement n'est pas encaissé. */
function estAnnulable(order: any): boolean {
  const statut = String(order?.status || "");
  if (statut === "CANCELLED" || statut === "GLOVO_CANCELLED") return false;
  if (statut === "GLOVO_DELIVERED") {
    return order?.source === "WEBSITE" && order?.paiementEncaisse !== true;
  }
  return true;
}

function AnnulerCommande({
  order,
  onDone,
  onError,
  ouvertAuDepart,
  onFermer,
}: {
  order: any;
  onDone: (message: string) => void;
  onError: (message: string) => void;
  ouvertAuDepart?: boolean;
  onFermer?: () => void;
}) {
  const [ouvert, setOuvert] = useState(!!ouvertAuDepart);
  const [motif, setMotif] = useState(MOTIFS_ANNULATION[0]);
  const [precision, setPrecision] = useState("");
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    setOuvert(!!ouvertAuDepart);
    setPrecision("");
  }, [order?.id, ouvertAuDepart]);

  const statut = String(order?.status || "");
  if (statut === "CANCELLED" || statut === "GLOVO_CANCELLED") {
    const a = order?.annulation;
    return a?.motif ? (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        Annulée{a.parNom ? ` par ${a.parNom}` : ""} — {a.motif}
      </div>
    ) : null;
  }
  // 02/11/2026 : une commande du site « livrée » mais pas encaissée
  // s'annule encore (le client appelle pour annuler). Encaissée, non : c'est
  // un remboursement. Le serveur applique la meme regle.
  if (statut === "GLOVO_DELIVERED" && !(order?.source === "WEBSITE" && order?.paiementEncaisse !== true)) {
    return null;
  }

  const glovo = order?.source !== "WEBSITE";
  return (
    <div className="rounded-lg border border-red-200 bg-white p-3">
      {!ouvert ? (
        <button
          onClick={() => setOuvert(true)}
          className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 flex items-center gap-2"
        >
          <XCircle className="w-4 h-4" />
          Annuler la commande
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-red-800">Annuler la commande — pourquoi ?</p>
          <select
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {MOTIFS_ANNULATION.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <input
            value={precision}
            onChange={(e) => setPrecision(e.target.value)}
            placeholder="Précision (ex. : pas de daurade aujourd'hui)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <p className="text-[11px] text-gray-600">
            Le client reçoit le message WhatsApp d&apos;annulation.
            {glovo
              ? " Commande Glovo : annule-la aussi dans le portail Glovo, l'application ne le fait pas à sa place."
              : " Commande du site : la course est aussi annulée chez les livreurs."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={enCours}
              onClick={async () => {
                setEnCours(true);
                try {
                  const raison = precision.trim() ? `${motif} — ${precision.trim()}` : motif;
                  await api.orders.cancel(order.id, raison);
                  onDone("Commande annulée.");
                  setOuvert(false);
                } catch (e: any) {
                  onError(e?.message || "Impossible d'annuler la commande.");
                } finally {
                  setEnCours(false);
                }
              }}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {enCours ? "…" : "Confirmer l'annulation"}
            </button>
            <button
              onClick={() => (onFermer ? onFermer() : setOuvert(false))}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:text-gray-800"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Remettre en préparation une commande du site passée « Livrée » par
 * erreur (17/10/2026) — direction et support, dans les 48 heures.
 * Ex. WC75539D : validée, puis paniers « validés à la place du
 * fournisseur », récupérés et livrée en 47 secondes — rien n'avait été
 * préparé. Les paniers repassent « en attente », les fournisseurs sont
 * prévenus, la commande repart chez le responsable en service. Le client
 * n'est pas prévenu.
 */
function RemettreEnPreparation({
  order,
  onDone,
  onError,
}: {
  order: any;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [confirmer, setConfirmer] = useState(false);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => setConfirmer(false), [order?.id]);

  if (order?.source !== "WEBSITE" || String(order?.status || "") !== "GLOVO_DELIVERED") {
    return null;
  }
  const livreeLe = (() => {
    const d: any = order?.deliveredAt;
    const ms = d?._seconds ? d._seconds * 1000 : d?.seconds ? d.seconds * 1000 : d ? Date.parse(d) : NaN;
    return Number.isFinite(ms) ? ms : null;
  })();
  if (livreeLe !== null && Date.now() - livreeLe > 48 * 3600_000) return null;

  const regularisee = !!order?.livreeHorsApplication;

  return (
    <div className="mt-3 rounded-md border border-amber-400 bg-amber-50 p-2">
      <p className="text-xs font-semibold text-amber-900">
        {regularisee
          ? "Régularisée « déjà livrée » — les fournisseurs n'ont rien préparé."
          : "Livrée par erreur, sans avoir été préparée ?"}
      </p>
      <p className="text-[11px] text-amber-800 mb-2">
        La commande repart en préparation : ses paniers repassent « en attente »
        et les fournisseurs sont prévenus. Le client ne reçoit rien.
      </p>
      {!confirmer ? (
        <button
          onClick={() => setConfirmer(true)}
          className="text-xs px-3 py-1 rounded border border-amber-500 bg-white text-amber-900 font-medium hover:bg-amber-100"
        >
          Remettre en préparation
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            disabled={enCours}
            onClick={async () => {
              setEnCours(true);
              try {
                const r = await api.orders.remettreEnPreparation(order.id);
                onDone(
                  `Commande remise en préparation — ${r.prevenus}/${r.paniers} fournisseur${
                    r.paniers > 1 ? "s" : ""
                  } prévenu${r.prevenus > 1 ? "s" : ""}.`,
                );
                setConfirmer(false);
              } catch (e: any) {
                onError(e?.message || "Remise en préparation impossible.");
              } finally {
                setEnCours(false);
              }
            }}
            className="text-xs px-3 py-1 rounded bg-amber-600 text-white font-medium disabled:opacity-50"
          >
            {enCours ? "…" : "Confirmer : remettre en préparation"}
          </button>
          <button
            onClick={() => setConfirmer(false)}
            className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Changer UN article de fournisseur (10/10/2026) — direction et support.
 * L'article rejoint le panier du bon fournisseur (et son historique) ;
 * l'ancien panier, s'il est vide, passe en annule. Rien n'est supprime.
 */
function DeplacerArticle({
  order,
  onDone,
  onError,
}: {
  order: any;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [fournisseurs, setFournisseurs] = useState<any[] | null>(null);
  // Les caisses du magasin : une par responsable commande (21/10/2026).
  const [caisses, setCaisses] = useState<Array<{ uid: string; nom: string }>>([]);
  const [itemId, setItemId] = useState("");
  const [cible, setCible] = useState("");
  // Le prix d'achat a l'unite, saisi quand l'article passe en caisse.
  const [prixAchat, setPrixAchat] = useState("");
  // Combien d'unites passent en caisse (29/10/2026) : 1 cardon sur 2, par
  // exemple. Toute la ligne par defaut.
  const [quantite, setQuantite] = useState(0);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    setOuvert(false);
    setItemId("");
    setCible("");
    setPrixAchat("");
    setQuantite(0);
  }, [order?.id]);

  // Le prix du fichier en guise de depart, modifiable.
  useEffect(() => {
    const it = (Array.isArray(order?.items) ? order.items : []).find((x: any) => x.id === itemId);
    const p = Number(it?.prixAchatFige) || 0;
    setPrixAchat(p > 0 ? String(p).replace(".", ",") : "");
    setQuantite(Math.max(1, Number(it?.quantity) || 1));
  }, [itemId, order?.items]);

  useEffect(() => {
    if (!ouvert || !order?.storeId) return;
    api.orders
      .equipe(order.storeId)
      .then((r: any) =>
        setCaisses(
          (Array.isArray(r?.equipe) ? r.equipe : []).map((e: any) => ({ uid: String(e.uid), nom: String(e.nom || "") })),
        ),
      )
      .catch(() => setCaisses([]));
  }, [ouvert, order?.storeId]);

  useEffect(() => {
    if (!ouvert || fournisseurs) return;
    api.fournisseurs
      .getAll(order?.storeId ? { storeId: order.storeId } : undefined)
      .then((l) => setFournisseurs(Array.isArray(l) ? l : []))
      .catch((e: any) => {
        setFournisseurs([]);
        onError(e?.message || "Liste des fournisseurs indisponible.");
      });
  }, [ouvert]);

  const items: any[] = Array.isArray(order?.items) ? order.items : [];
  const baskets: any[] = Array.isArray(order?.baskets) ? order.baskets : [];
  const nomPanier = (basketId: string, it?: any) => {
    if (it?.achatCaisse && !basketId) return "la caisse";
    const b = baskets.find((x) => x.id === basketId);
    return b?.fournisseurName || "sans fournisseur";
  };
  const nomFournisseur = (f: any) =>
    f?.user?.name || f?.user?.username || f?.businessName || f?.id;

  if (items.length === 0) return null;
  const totalLigne = Math.max(1, Number(items.find((x) => x.id === itemId)?.quantity) || 1);

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="mt-3 text-xs px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
      >
        Changer un article de fournisseur
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-white p-2">
      <div className="text-xs font-semibold text-amber-900 mb-1">
        Changer un article de fournisseur
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[260px]"
        >
          <option value="">Article…</option>
          {items.map((it) => (
            <option key={it.id} value={it.id}>
              {it.quantity || 1} × {it.productName || it.name || "Article"} — chez{" "}
              {nomPanier(it.basketId, it)}
              {Number(it.quantiteCaisse) > 0 ? ` (dont ${it.quantiteCaisse} en caisse)` : ""}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-500">→</span>
        <select
          value={cible}
          onChange={(e) => setCible(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[200px]"
        >
          <option value="">{fournisseurs ? "Fournisseur…" : "Chargement…"}</option>
          {caisses.length > 1 ? (
            caisses.map((c) => (
              <option key={c.uid} value={`CAISSE:${c.uid}`}>
                🧾 Caisse de {c.nom}
              </option>
            ))
          ) : caisses.length === 1 ? (
            <option value={`CAISSE:${caisses[0].uid}`}>🧾 Caisse de {caisses[0].nom}</option>
          ) : (
            <option value="CAISSE">
              🧾 Caisse de {String(order?.responsableNom || "").trim() || "responsable de la commande"}
            </option>
          )}
          {(fournisseurs || []).map((f) => (
            <option key={f.id} value={f.id}>
              {nomFournisseur(f)}
            </option>
          ))}
        </select>
        {cible.startsWith("CAISSE") && totalLigne > 1 && (
          <label className="flex items-center gap-1 text-xs text-gray-700">
            Combien
            <select
              value={quantite}
              onChange={(e) => setQuantite(Number(e.target.value))}
              className="border border-amber-400 rounded px-1 py-1 text-xs font-semibold"
            >
              {Array.from({ length: totalLigne }, (_, k) => k + 1).map((n) => (
                <option key={n} value={n}>
                  {n === totalLigne ? `les ${n}` : `${n} sur ${totalLigne}`}
                </option>
              ))}
            </select>
          </label>
        )}
        {cible.startsWith("CAISSE") && (
          <label className="flex items-center gap-1 text-xs text-gray-700">
            Prix d&apos;achat
            <input
              inputMode="decimal"
              value={prixAchat}
              onChange={(e) => setPrixAchat(e.target.value)}
              placeholder="DH / unité"
              className="w-24 border border-amber-400 rounded px-2 py-1 text-xs font-semibold"
            />
            DH / unité
          </label>
        )}
        <button
          disabled={
            enCours ||
            !itemId ||
            !cible ||
            (cible.startsWith("CAISSE") && !(Number(String(prixAchat).replace(",", ".")) > 0))
          }
          onClick={async () => {
            setEnCours(true);
            try {
              const prix = Number(String(prixAchat).replace(",", "."));
              const partiel = cible.startsWith("CAISSE") && quantite > 0 && quantite < totalLigne;
              // Une partie va dans une caisse NOMMEE : avec une seule
              // caisse au magasin, c'est la sienne.
              const cibleEnvoyee =
                partiel && cible === "CAISSE" && caisses.length === 1 ? `CAISSE:${caisses[0].uid}` : cible;
              const r = await api.orders.deplacerArticle(
                order.id,
                itemId,
                cibleEnvoyee,
                cible.startsWith("CAISSE") && prix > 0 ? prix : undefined,
                partiel ? quantite : undefined,
              );
              const f = (fournisseurs || []).find((x) => x.id === cible);
              if (r.partiel) {
                onDone(
                  `${r.partiel.quantite} sur ${r.partiel.sur} payé(s) en caisse${
                    cible.startsWith("CAISSE:")
                      ? ` (caisse de ${caisses.find((c) => `CAISSE:${c.uid}` === cible)?.nom || "?"})`
                      : ""
                  } — le reste reste chez le fournisseur.`,
                );
                setItemId("");
                setCible("");
                setOuvert(false);
                return;
              }
              onDone(
                (cible.startsWith("CAISSE")
                  ? `Article passé en achat caisse${
                      cible.startsWith("CAISSE:")
                        ? ` (caisse de ${caisses.find((c) => `CAISSE:${c.uid}` === cible)?.nom || "?"})`
                        : ""
                    } : à acheter et saisir le prix`
                  : `Article déplacé chez ${nomFournisseur(f)}`) +
                  (r.ancienAnnule ? " — l'ancien panier, vide, est annulé." : "."),
              );
              setItemId("");
              setCible("");
              setOuvert(false);
            } catch (e: any) {
              onError(e?.message || "Déplacement impossible.");
            } finally {
              setEnCours(false);
            }
          }}
          className="text-xs px-3 py-1 rounded bg-amber-600 text-white font-medium disabled:opacity-50"
        >
          {enCours ? "…" : "Déplacer"}
        </button>
        <button
          onClick={() => setOuvert(false)}
          className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
        >
          Fermer
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mt-1">
        Corrige cette commande seulement. Pour les prochaines, corrige aussi
        le fournisseur de l&apos;article dans le fichier de prix.
      </p>
    </div>
  );
}

export default function OrdersPage() {
  const { user } = useAuth();

  // Cet ecran sert l'admin, le support ET le responsable commande. Cette
  // ligne est ce qui permet de retirer a l'un ce que les autres gardent.
  const estCollaborateur = user?.role === UserRole.COLLABORATEUR;
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();
  const [searchParams] = useSearchParams();
  const phoneFilter = searchParams.get("phone") || "";

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  // Annuler directement depuis la liste (02/11/2026), sans ouvrir « Voir ».
  const [annulerDepuisListe, setAnnulerDepuisListe] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "ALL">("ALL");
  const [storeFilter, setStoreFilter] = useState<string>("ALL");
  // Glovo / Kooul : deux plateformes, jamais melangees
  const [plateformeFilter, setPlateformeFilter] = useState<
    "ALL" | "GLOVO" | "WEBSITE" | "KOOUL"
  >("ALL");
  /** Saisie du salaire du responsable, dans la tuile « Coût d'une commande ». */
  const [salaireSaisi, setSalaireSaisi] = useState<string | null>(null);
  const [salaireEnvoi, setSalaireEnvoi] = useState(false);
  /** Detail poste par poste du cout d'une commande (IA, telephonie...). */
  const [detailCharges, setDetailCharges] = useState(false);
  // Aujourd'hui par defaut : c'est ce qu'on vient regarder en arrivant.
  // L'historique complet reste accessible par le bouton Periode.
  const [dateFilter, setDateFilter] = useState<DateFilter>("today");
  const [periodeOuverte, setPeriodeOuverte] = useState(false);
  /** Premier jour clique dans le calendrier, en attendant le dernier. */
  const [debutPlage, setDebutPlage] = useState<string | null>(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  // Ce que tu tapes, et ce qui part au serveur. Les deux sont separes pour
  // ne pas declencher une requete a chaque lettre.
  const [codeSaisi, setCodeSaisi] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  const [detailPerdues, setDetailPerdues] = useState<any | null>(null);
  const [chargementPerdues, setChargementPerdues] = useState(false);
  const [detailDeductions, setDetailDeductions] = useState<any | null>(null);
  const [chargementDeductions, setChargementDeductions] = useState(false);
  const [photosOuvertes, setPhotosOuvertes] = useState<Record<string, any[]>>({});
  // Le detail des ventes perdues, charge une fois pour les deux cartes.
  // Sans lui, la carte des erreurs de validation ne pourrait afficher aucun
  // chiffre avant qu'on l'ouvre — et un indicateur qu'il faut ouvrir pour
  // savoir s'il alerte n'alerte personne.
  const [resumePerdues, setResumePerdues] = useState<any | null>(null);
  const [moisAffiche, setMoisAffiche] = useState(() => {
    const n = new Date();
    return { annee: n.getFullYear(), mois: n.getMonth() };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [processing, setProcessing] = useState<string | null>(null);
  const [userStoreId, setUserStoreId] = useState<string | null | "loading">(
    "loading",
  );

  const ITEMS_PER_PAGE = 30;

  // ✅ React Query pour stores
  const { data: stores = [], isLoading: storesLoading } = useStores();

  // ✅ Stats des commandes (optimisé, un seul appel)
  const statsFilters = useMemo(
    () => ({
      storeId: storeFilter !== "ALL" ? storeFilter : undefined,
      dateFilter: dateFilter,
      // Les tuiles suivent la plateforme choisie (01/11/2026) : avant,
      // « Glovo » comptait aussi les commandes du site.
      source: plateformeFilter !== "ALL" ? plateformeFilter : undefined,
    }),
    [storeFilter, dateFilter, plateformeFilter],
  );

  // LES QUINZAINES CLOTUREES (02/11/2026) : leurs chiffres sont gardes par
  // le serveur (une lecture), et l'ecran cesse de les redemander.
  const [quinzaines, setQuinzaines] = useState<
    Array<{ cle: string; filtre: string; cloturee: boolean; terminee: boolean }>
  >([]);
  const [clotureEnCours, setClotureEnCours] = useState(false);
  const chargerQuinzaines = useCallback(() => {
    api.orders
      .clotures()
      .then((r) => setQuinzaines(Array.isArray(r?.quinzaines) ? r.quinzaines : []))
      .catch(() => setQuinzaines([]));
  }, []);
  useEffect(() => {
    chargerQuinzaines();
  }, [chargerQuinzaines]);
  const quinzaineCourante = quinzaines.find((q) => q.filtre === dateFilter) || null;
  const periodeFigee = !!quinzaineCourante?.cloturee;

  const { data: stats } = useOrderStats(statsFilters, { figee: periodeFigee });

  // LES ANNULEES EN DIRHAMS, PAR RESPONSABLE (02/11/2026) : lues a chaque
  // changement de periode, de magasin ou de plateforme — pas toutes les
  // minutes.
  const [annulations, setAnnulations] = useState<any | null>(null);
  const [annulationsOuvertes, setAnnulationsOuvertes] = useState(false);
  useEffect(() => {
    let fini = false;
    api.orders
      .annulations({
        storeId: storeFilter !== "ALL" ? storeFilter : undefined,
        dateFilter,
        source: plateformeFilter !== "ALL" ? plateformeFilter : undefined,
      })
      .then((r) => {
        if (!fini) setAnnulations(r);
      })
      .catch(() => {
        if (!fini) setAnnulations(null);
      });
    return () => {
      fini = true;
    };
  }, [storeFilter, dateFilter, plateformeFilter]);

  // Date du dernier fichier Glovo importe. Sans elle, impossible de savoir si
  // les montants nets et les ventes perdues affiches sont d'aujourd'hui ou
  // d'avant-hier — un chiffre sans date n'est pas un chiffre fiable.
  const [dernierImport, setDernierImport] = useState<{
    quand: string | null;
    commandesLues: number;
  } | null>(null);

  useEffect(() => {
    let annule = false;
    api.importGlovo
      .journal(1)
      .then((r) => {
        if (!annule && r.lignes.length > 0) {
          setDernierImport({
            quand: r.lignes[0].quand,
            commandesLues: r.lignes[0].commandesLues,
          });
        }
      })
      .catch(() => {
        /* information de confort : son absence ne doit rien casser */
      });
    return () => {
      annule = true;
    };
  }, []);

  // Récupérer le storeId du collaborateur/fournisseur (nécessaire pour Firestore rules)
  useEffect(() => {
    if (!user) return;
    const u = user;
    if (u.role === UserRole.ADMIN || u.role === UserRole.SUPPORT) {
      setUserStoreId(null);
      return;
    }
    async function fetchUserStore() {
      try {
        if (u.role === UserRole.COLLABORATEUR) {
          const collabs = await getDocuments(
            COLLECTIONS.COLLABORATEURS,
            [{ field: "userId", operator: "==", value: u.uid }],
            undefined,
            "asc",
            1,
          );
          setUserStoreId(collabs[0]?.storeId ?? null);
        } else if (u.role === UserRole.FOURNISSEUR) {
          const fourns = await getDocuments(
            COLLECTIONS.FOURNISSEURS,
            [{ field: "userId", operator: "==", value: u.uid }],
            undefined,
            "asc",
            1,
          );
          setUserStoreId(fourns[0]?.storeId ?? null);
        } else {
          setUserStoreId(null);
        }
      } catch {
        setUserStoreId(null);
      }
    }
    fetchUserStore();
  }, [user?.uid, user?.role]);

  // ✅ Pagination serveur : status + storeId + date + phone filtrés côté serveur
  const ordersFilters = useMemo(() => {
    if (userStoreId === "loading") return undefined;
    return {
      storeId: storeFilter !== "ALL" ? storeFilter : (userStoreId ?? undefined),
      status: statusFilter !== "ALL" ? statusFilter : undefined,
      dateFilter: dateFilter,
      phone: phoneFilter || undefined, // 🔥 Filtre phone côté serveur (même avec storeId=ALL)
      code: codeFilter || undefined,
      // La plateforme est filtree PAR LE SERVEUR, sur toute la periode —
      // pas seulement sur les 30 commandes de la page affichee.
      source: plateformeFilter !== "ALL" ? plateformeFilter : undefined,
      limit: ITEMS_PER_PAGE,
      page: currentPage,
    };
  }, [
    userStoreId,
    statusFilter,
    storeFilter,
    dateFilter,
    phoneFilter,
    codeFilter,
    plateformeFilter,
    currentPage,
  ]);

  const ordersReady = userStoreId !== "loading";

  // ✅ React Query pour orders
  const { data: ordersResponse, isLoading: ordersLoading } =
    useOrdersQuery(ordersFilters);
  const loading = ordersLoading || storesLoading;

  const storeMap = useMemo(
    () => new Map(stores.map((s) => [s.id, s])),
    [stores],
  );

  // ✅ Extraire orders et pagination
  const rawOrders = useMemo(() => {
    if (!ordersResponse) return [];
    return (ordersResponse as any).orders || [];
  }, [ordersResponse]);

  const pagination = useMemo(() => {
    if (!ordersResponse) return null;
    return (ordersResponse as any).pagination || null;
  }, [ordersResponse]);

  // ✅ Ajouter store info (tous les filtres sont côté serveur maintenant)
  const filteredOrders = useMemo(() => {
    return rawOrders
      .filter((o: any) =>
        // Une recherche par numero ignore la plateforme, comme la periode
        // et le magasin : on cherche une commande precise, ou qu'elle soit.
        plateformeFilter === "ALL" || codeFilter
          ? true
          : (o.source || "GLOVO") === plateformeFilter,
      )
      .map((o: any) => ({
        ...o,
        store: o.storeId ? storeMap.get(o.storeId) : null,
      }));
  }, [rawOrders, storeMap, plateformeFilter, codeFilter]);

  // ✅ Pagination serveur
  const totalPages = pagination?.totalPages || 1;
  const totalOrders = pagination?.total || filteredOrders.length;
  const orders = filteredOrders;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, storeFilter, dateFilter, codeFilter, plateformeFilter]);

  function formatPrice(amount: number) {
    return `${(amount / 100).toFixed(2)} MAD`;
  }

  function formatDate(date: any) {
    if (!date) return "N/A";
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      // TOUJOURS L'HEURE DE CASABLANCA.
      //
      // Sans fuseau precise, le navigateur affiche celui de la machine : un
      // poste regle sur Paris annonce une commande de 16 h 44 comme arrivee
      // a 17 h 44, et le delai de preparation se lit a l'envers.
      return d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: FUSEAU_MAROC,
      });
    } catch {
      return "N/A";
    }
  }

  async function handleAccept(orderId: string) {
    try {
      setProcessing(orderId);
      const res: any = await api.orders.accept(orderId);
      
      // ✅ Invalider le cache React Query pour refetch automatiquement
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orderStats"] });
      
      // ✅ Refetch la commande pour mettre à jour le modal si ouvert
      if (selectedOrder?.id === orderId) {
        const updatedOrder = await api.orders.getById(orderId);
        setSelectedOrder(updatedOrder);
      }
      
      if (res?.glovoApiCalled === false) {
        console.error("[GLOVO] Echec acceptation :", res?.glovoError);
        showToast(
          "Acceptée dans l'application, mais Glovo n'a PAS été prévenu. Accepte-la aussi sur le semi.",
          "error",
        );
      } else {
        showToast("Commande acceptée et transmise à Glovo", "success");
        setSelectedOrder(null);
      }
    } catch (error: any) {
      console.error("Error accepting order:", error);
      showToast(
        error.message || "Impossible d'accepter la commande",
        "error"
      );
    } finally {
      setProcessing(null);
    }
  }

  async function handleMarkReady(orderId: string) {
    try {
      setProcessing(orderId);
      const res: any = await api.orders.markReady(orderId);
      
      // ✅ Invalider le cache React Query
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orderStats"] });
      
      if (selectedOrder?.id === orderId) {
        const updatedOrder = await api.orders.getById(orderId);
        setSelectedOrder(updatedOrder);
      }
      
      if (res?.glovoApiCalled === false) {
        console.error("[GLOVO] Echec marquage prête :", res?.glovoError);
        showToast(
          "Marquée prête dans l'application, mais Glovo n'a PAS été prévenu. Marque-la aussi sur le semi.",
          "error",
        );
      } else {
        showToast("Commande marquée prête et transmise à Glovo", "success");
      }
    } catch (error: any) {
      console.error("Error marking ready:", error);
      showToast(
        error.message || "Impossible de marquer la commande comme prête",
        "error"
      );
    } finally {
      setProcessing(null);
    }
  }

  async function handleBasketMarkReady(basketId: string) {
    try {
      setProcessing(basketId);
      await api.baskets.markReady(basketId);
      
      // ✅ Invalider le cache React Query
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      
      if (selectedOrder) {
        const updatedOrder = await api.orders.getById(selectedOrder.id);
        setSelectedOrder(updatedOrder);
      }
      
      showToast("Panier marqué comme prêt !", "success");
    } catch (error: any) {
      console.error("Error marking basket ready:", error);
      showToast(
        error.message || "Impossible de marquer le panier comme prêt",
        "error"
      );
    } finally {
      setProcessing(null);
    }
  }

  async function handleBasketPickup(basketId: string) {
    try {
      setProcessing(basketId);
      await api.baskets.pickup(basketId);
      if (selectedOrder) {
        const updatedOrder = await api.orders.getById(selectedOrder.id);
        setSelectedOrder(updatedOrder);
      }
      alert("Panier récupéré !");
    } catch (error: any) {
      console.error("Error picking up basket:", error);
      alert(`Erreur: ${error.message || "Impossible de récupérer le panier"}`);
    } finally {
      setProcessing(null);
    }
  }

  function handlePhoneSaved() {
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    showToast("Numero du client enregistre", "success");
  }

  async function handleCancel(orderId: string) {
    if (!confirm("Êtes-vous sûr de vouloir annuler cette commande ?")) return;
    try {
      setProcessing(orderId);
      await api.orders.cancel(orderId);
      
      // ✅ Invalider le cache React Query
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orderStats"] });
      
      if (selectedOrder?.id === orderId) {
        const updatedOrder = await api.orders.getById(orderId);
        setSelectedOrder(updatedOrder);
      }
      
      showToast("Commande annulée", "success");
      setSelectedOrder(null);
    } catch (error: any) {
      console.error("Error cancelling order:", error);
      showToast(
        error.message || "Impossible d'annuler la commande",
        "error"
      );
    } finally {
      setProcessing(null);
    }
  }

  const isCollaborateur = user?.role === UserRole.COLLABORATEUR;
  const isFournisseur = user?.role === UserRole.FOURNISSEUR;
  const canManageOrders = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPPORT || isCollaborateur;
  // Saisie du telephone client : reservee a ADMIN et SUPPORT (permission order.edit_phone)
  const canEditPhone =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPPORT;
  // LES PRIX (02/11/2026), regle du patron : ni prix de vente ni prix
  // d'achat pour le responsable commande. Le serveur ne les lui envoie
  // d'ailleurs plus ; l'ecran ne garde pas de case vide.
  const voitPrix = canEditPhone;


  // Les deux effets ci-dessous doivent rester AVANT le return conditionnel
  // ci-dessous : React exige le meme nombre de hooks a chaque rendu.
  // Recharge a chaque changement de periode ou de magasin, comme les cartes.
  useEffect(() => {
    let annule = false;
    api.orders
      .getVentesPerdues({
        storeId: storeFilter !== "ALL" ? storeFilter : undefined,
        dateFilter,
      })
      .then((r) => {
        if (!annule) setResumePerdues(r);
      })
      .catch(() => {
        // Un resume indisponible ne doit pas casser la page : les cartes
        // afficheront simplement un tiret.
        if (!annule) setResumePerdues(null);
      });
    return () => {
      annule = true;
    };
  }, [storeFilter, dateFilter]);

  // Un demi-seconde de silence avant d'interroger le serveur : on cherche
  // un numero, pas ses prefixes.
  useEffect(() => {
    const t = setTimeout(() => setCodeFilter(codeSaisi.trim()), 500);
    return () => clearTimeout(t);
  }, [codeSaisi]);

  if (!ordersReady || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  /**
   * Ce que l'import Glovo a depose sur une commande.
   *
   * Tant qu'aucun fichier n'a ete importe, ces champs n'existent pas : on
   * renvoie alors des valeurs vides et l'affichage retombe sur le montant
   * de la commande. Aucun ecran ne doit dependre d'un import qui n'a pas
   * encore eu lieu.
   */
  /**
   * Le temps de preparation d'une commande — CELUI DU FICHIER GLOVO.
   *
   * La regle de calcul est celle de Glovo, et d'elle seule : de l'heure ou
   * la commande a ete ACCEPTEE a l'heure ou elle a ete declaree PRETE. A
   * defaut d'heure d'acceptation, on part de l'heure de reception. Ces deux
   * horodatages viennent du fichier, ils ne sont pas reconstitues ici.
   *
   * L'ecran affichait avant un chronometre en direct — les minutes ecoulees
   * depuis la commande, qui montaient tant que rien ne la terminait. Ce
   * n'etait PAS un temps de preparation : une commande deja livree dont le
   * passage a « prete » n'avait pas ete enregistre affichait 203 min. Un
   * chiffre qui grandit tout seul finit par etre pris pour une mesure.
   *
   * Tant que le fichier n'est pas importe, il n'y a donc rien a afficher —
   * et c'est ce qu'on dit, plutot que d'inventer un nombre.
   */
  const tempsPreparation = (order: any) => {
    const g = order?.metadata?.glovo || {};
    const fini =
      g.preparationDepuisAccepteeMinutes ?? g.preparationMinutes ?? null;

    if (typeof fini === "number" && Number.isFinite(fini)) {
      return { minutes: fini as number | null, enCours: false, enAttente: false };
    }

    return { minutes: null as number | null, enCours: false, enAttente: true };
  };

  const infosGlovo = (order: any) => {
    const g = order?.metadata?.glovo || {};
    const statut = String(g.statut || "").toLowerCase();

    // Un net a zero veut dire deux choses opposees : soit Glovo ne versera
    // rien (commande annulee, ou frais qui absorbent tout), soit la commande
    // n'est pas encore soldee et le montant n'est pas connu. Le montant seul
    // ne permet pas de trancher — le statut, si.
    const soldee = statut.startsWith("livr") || statut.startsWith("annul");
    const net = soldee ? Number(g.revenusEstimesCentimes ?? 0) : null;

    return {
      /** Net que Glovo versera, ou null tant que ce n'est pas tranche. */
      net,
      // Le champ ecrit a plat par l'import : il additionne les frais
      // operationnels (la ou Glovo range reellement ses retenues) et sa
      // colonne « Remboursements par le partenaire », qui reste vide.
      // L'ancienne lecture sert de secours pour les commandes importees
      // avant ce changement.
      remboursement: Number(
        order?.remboursementCentimes ?? g.remboursementPartenaireCentimes ?? 0,
      ),
      preparationMinutes:
        g.preparationDepuisAccepteeMinutes ?? g.preparationMinutes ?? null,
    };
  };

  // Le montant en entier, jamais abrege.
  //
  // « 9.4k » se lisait plus vite mais ne se verifiait pas : impossible de le
  // rapprocher d'un fichier Glovo. Sur un tableau de bord d'exploitation, le
  // gain de place ne vaut pas la perte de certitude.
  //
  // Les milliers sont separes par une espace insecable etroite — la
  // convention francaise — pour que 9363 se lise 9 363 sans se casser en fin
  // de ligne.
  const formatPriceCompact = (amount: number) => {
    const value = Math.round(amount / 100);
    return value.toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, "\u202F");
  };

  /** Les commandes dont une part du montant ne s'explique par aucun
   *  article retire. Ce n'est pas une rupture de stock : c'est une saisie
   *  fausse — trop basse, ou trop haute.
   *
   *  On lit `ecartValidationCentimes`, et lui seul : c'est exactement la
   *  part inexpliquee. Compter la perte entiere d'une commande sans article
   *  identifie melangeait deux choses differentes — une saisie fausse, et
   *  un rapprochement de noms en echec. */
  const erreursValidation: any[] = (resumePerdues?.commandes || []).filter(
    (c: any) => Number(c.ecartValidationCentimes || 0) > 0,
  );

  // Trop bas : la maison a perdu. Trop haut : elle a encaisse en plus.
  const validationEnMoins = erreursValidation
    .filter((c: any) => c.sensValidation !== "HAUT")
    .reduce((s: number, c: any) => s + Number(c.ecartValidationCentimes), 0);
  const validationEnTrop = erreursValidation
    .filter((c: any) => c.sensValidation === "HAUT")
    .reduce((s: number, c: any) => s + Number(c.ecartValidationCentimes), 0);

  // Le solde, signe : negatif quand la maison est perdante, positif quand
  // les erreurs lui ont rapporte.
  const soldeValidation = validationEnTrop - validationEnMoins;
  const totalValidation = soldeValidation;

  const periodStats = stats?.period;
  const ordersAll = periodStats?.ordersAll ?? periodStats?.orders ?? 0;
  const ordersNet = periodStats?.orders ?? 0;
  const cancelledOrd = periodStats?.cancelledOrders ?? 0;
  const revenueNet = periodStats?.revenue ?? 0;
  const revenueAll = periodStats?.revenueAll ?? periodStats?.revenue ?? 0;
  const cancelledRev = periodStats?.cancelledRevenue ?? 0;

  // Le serveur envoie-t-il un chiffre d'affaires ?
  //
  // Il ne l'envoie pas aux collaborateurs. On ne teste pas le ROLE ici mais
  // la PRESENCE du chiffre : l'ecran affiche ce qu'il a recu, et n'a pas a
  // connaitre la regle. Le jour ou un autre profil sera concerne, il n'y
  // aura rien a changer de ce cote.
  //
  // Et surtout : pas de tuile « REVENUS 0,00 MAD ». Un zero se lit comme un
  // chiffre vrai — c'est pire que pas de tuile du tout.
  const caDisponible =
    periodStats != null && typeof (periodStats as any).revenue === "number";
  // Ces deux montants viennent de l'import du fichier Glovo. Tant qu'aucun
  // import n'a eu lieu sur la periode affichee, ils valent zero — c'est
  // normal, ce n'est pas une panne.
  const lostSales = periodStats?.lostSales ?? 0;
  const refunds = periodStats?.refunds ?? 0;
  /**
   * Ce que Glovo a retenu DEPUIS LE 1er DU MOIS.
   *
   * « 40 DH aujourd'hui » ne dit pas si le mois derape ; « 40 DH
   * aujourd'hui, 256 DH depuis le 1er » le dit. Le chiffre du jour se
   * regarde sans jamais etre compare — c'est ainsi qu'un mois se perd en
   * detail.
   */
  const refundsMois = (stats as any)?.mois?.refunds ?? null;

  // Calculer les stats par statut
  const pendingCount = stats?.byStatus?.PENDING?.count || 0;
  const inProgressCount =
    (stats?.byStatus?.GLOVO_ACCEPTED?.count || 0) +
    (stats?.byStatus?.IN_PREPARATION?.count || 0);
  const readyCount = stats?.byStatus?.GLOVO_READY?.count || 0;
  const deliveredCount = stats?.byStatus?.GLOVO_DELIVERED?.count || 0;
  const cancelledCount =
    (stats?.byStatus?.CANCELLED?.count || 0) +
    (stats?.byStatus?.GLOVO_CANCELLED?.count || 0);

  // La moyenne de preparation.
  //
  // Elle ne compte QUE les commandes terminees. C'est le point important :
  // une commande arrivee il y a une minute est « en preparation depuis
  // 1 min », et la compter tirerait la moyenne vers le bas sans qu'aucune
  // preparation ait eu lieu. On mesure ce qui est fini, pas ce qui commence.
  //
  // Elle porte sur les commandes AFFICHEES — celles que les filtres ont
  // laissees passer. Filtrer par magasin donne donc la moyenne de ce
  // magasin, ce qui est exactement ce qu'on veut comparer.
  const preparationMoyenne = (() => {
    const finies = orders
      .map((o: any) => tempsPreparation(o))
      .filter((t: any) => t && !t.enCours && Number.isFinite(t.minutes))
      .map((t: any) => t.minutes as number);

    if (finies.length === 0) return null;

    const total = finies.reduce((a: number, b: number) => a + b, 0);
    return {
      minutes: Math.round(total / finies.length),
      surCombien: finies.length,
    };
  })();

  return (
    <div className="space-y-4">
      {/* Les retraits d'article qui attendent le patron (21/10/2026). Une
          commande attend derriere chacun : en tete de page. */}
      {(user?.role === UserRole.ADMIN || user?.role === UserRole.SUPPORT) && (
        <RetraitsCommande />
      )}
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-8 gap-3">
        {/* Total Commandes */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-blue-100 text-xs font-medium uppercase tracking-wide">
                Commandes
              </p>
              <p className="text-2xl md:text-3xl font-bold mt-1 tabular-nums">
                {ordersNet.toLocaleString("fr-FR")}
              </p>
              {cancelledOrd > 0 && (
                <p className="text-blue-100 text-xs mt-1.5 leading-snug tabular-nums">
                  <span className="font-medium text-white/90">
                    {ordersAll.toLocaleString("fr-FR")}
                  </span>
                  <span className="text-blue-100/95"> − </span>
                  <span className="font-semibold text-white/95">
                    {cancelledOrd.toLocaleString("fr-FR")} annulées
                  </span>
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
              <Package className="w-5 h-5" />
            </div>
          </div>
          <p className="text-blue-100 text-xs mt-2">
            {libellePeriode(dateFilter)}
          </p>
        </div>

        {/* Revenue */}
        {caDisponible && (
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-green-100 text-xs font-medium uppercase tracking-wide">
                Revenus
              </p>
              <p className="text-2xl md:text-3xl font-bold mt-1 tabular-nums">
                <span className="tabular-nums">
                  {formatPriceCompact(revenueNet)}
                </span>
              </p>
              {cancelledRev > 0 && (
                <p className="text-green-100 text-xs mt-1.5 leading-snug tabular-nums flex flex-wrap items-baseline gap-x-1">
                  <span className="font-medium text-white/90">
                    {formatPriceCompact(revenueAll)}
                  </span>
                  <span className="text-green-100/95">−</span>
                  <span className="font-semibold text-white/95">
                    {formatPriceCompact(cancelledRev)} annul.
                  </span>
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <p className="text-green-100 text-xs mt-2">MAD</p>
        </div>
        )}

        {/* Ventes perdues — articles retirés en préparation.
            Alimenté par l'import du fichier Glovo : vaut 0 tant qu'aucun
            import n'a été fait sur la période affichée. */}
        <button
          type="button"
          disabled={lostSales <= 0}
          onClick={async () => {
            setChargementPerdues(true);
            try {
              const r = await api.orders.getVentesPerdues({
                storeId: storeFilter !== "ALL" ? storeFilter : undefined,
                dateFilter,
              });
              setDetailPerdues(r);
            } catch (e: any) {
              // On ne remplace pas un chiffre juste par une liste inventee.
              alert(`Impossible de charger le détail : ${e.message}`);
            } finally {
              setChargementPerdues(false);
            }
          }}
          className={`text-left bg-white rounded-xl p-4 border border-gray-200 shadow-sm transition-colors ${
            lostSales > 0
              ? "hover:border-red-300 hover:bg-red-50/40 cursor-pointer"
              : "cursor-default"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Ventes perdues
              </p>
              <p className="text-2xl md:text-3xl font-bold text-red-600 mt-1 tabular-nums">
                {chargementPerdues ? "…" : formatPriceCompact(lostSales)}
              </p>
              <p className="text-gray-400 text-xs mt-1.5 leading-snug">
                {lostSales > 0 ? "articles retirés — voir le détail" : "aucun retrait"}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </button>

        {detailPerdues && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setDetailPerdues(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {detailPerdues.titre || "Ventes perdues"} —{" "}
                    {libellePeriode(dateFilter)}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {detailPerdues.commandes.length} commande
                    {detailPerdues.commandes.length > 1 ? "s" : ""} ·{" "}
                    <span className="font-semibold text-red-600 tabular-nums">
                      {formatPriceCompact(detailPerdues.total)} MAD
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => setDetailPerdues(null)}
                  className="p-1 rounded hover:bg-gray-100"
                  aria-label="Fermer"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {detailPerdues.tronquee && (
                <p className="px-5 py-2 text-xs text-orange-700 bg-orange-50 border-b border-orange-100">
                  Période trop large : seules les 500 commandes les plus
                  récentes ont été examinées. Choisis une période plus courte
                  pour une liste complète.
                </p>
              )}

              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {detailPerdues.commandes.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Aucun retrait d'article sur cette période.
                  </p>
                )}

                {/* Deux causes, deux traitements.
                    Une commande dont AUCUN article n'a ete retire, alors que
                    le montant a baisse, ne peut pas etre une rupture de
                    stock : le montant a ete saisi plus bas a la validation.
                    C'est une erreur humaine, et elle se repete tant que
                    personne ne la voit. */}
                {(() => {
                  const validation = detailPerdues.commandes.filter(
                    (c: any) => Number(c.ecartValidationCentimes || 0) > 0,
                  );
                  if (validation.length === 0) return null;

                  const enMoins = validation
                    .filter((c: any) => c.sensValidation !== "HAUT")
                    .reduce(
                      (s: number, c: any) =>
                        s + Number(c.ecartValidationCentimes),
                      0,
                    );
                  const enTrop = validation
                    .filter((c: any) => c.sensValidation === "HAUT")
                    .reduce(
                      (s: number, c: any) =>
                        s + Number(c.ecartValidationCentimes),
                      0,
                    );
                  const solde = enTrop - enMoins;
                  const gagnant = solde >= 0;

                  return (
                    <div
                      className={`rounded-lg border p-3 ${
                        gagnant
                          ? "border-green-200 bg-green-50"
                          : "border-red-200 bg-red-50"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          gagnant ? "text-green-800" : "text-red-800"
                        }`}
                      >
                        {validation.length} erreur
                        {validation.length > 1 ? "s" : ""} de validation ·
                        solde {solde > 0 ? "+" : solde < 0 ? "−" : ""}
                        {formatPriceCompact(Math.abs(solde))} MAD
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          gagnant ? "text-green-700" : "text-red-700"
                        }`}
                      >
                        {enMoins > 0 &&
                          `${formatPriceCompact(enMoins)} MAD validés sous le montant commandé. `}
                        {enTrop > 0 &&
                          `${formatPriceCompact(enTrop)} MAD validés au-dessus — encaissés en plus. `}
                        À vérifier avec le responsable du magasin.
                      </p>
                    </div>
                  );
                })()}

                {detailPerdues.commandes.map((c: any) => {
                  // Somme des articles identifiés. Le rapprochement se fait
                  // par nom de produit : il peut en manquer. Le montant, lui,
                  // vient de la différence de montants et reste exact.
                  const sommeArticles = c.articles.reduce(
                    (s: number, a: any) =>
                      s + Number(a.prixCentimes || 0) * Number(a.quantite || 1),
                    0,
                  );
                  const ecart = c.perduCentimes - sommeArticles;
                  // VALIDEE TROP HAUT (20/10/2026) : rien n'est perdu, la
                  // commande a ete encaissee AU-DESSUS du montant commande.
                  // La carte disait « −0 MAD … inferieur de 0 MAD ».
                  const tropHaut = c.sensValidation === "HAUT";
                  const ecartHaut = Number(c.ecartValidationCentimes || 0);

                  // Le nom du magasin, pas son identifiant. « e8Mn6vPL4… »
                  // ne dit rien a personne ; « Bourgogne » dit a qui
                  // telephoner.
                  const nomMagasin =
                    stores.find((s: any) => s.id === c.magasin)?.name ||
                    "magasin inconnu";

                  return (
                    <div
                      key={c.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {c.code}
                          </span>
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                            {nomMagasin}
                          </span>
                          {/* La cause, lisible sans ouvrir le detail. */}
                          <span
                            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                              tropHaut
                                ? "bg-emerald-100 text-emerald-700"
                                : c.articles.length === 0
                                  ? "bg-red-100 text-red-700"
                                  : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {tropHaut
                              ? "validée trop haut"
                              : c.articles.length === 0
                                ? "erreur de validation"
                                : "produit indisponible"}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {c.recueA
                              ? `reçue à ${c.recueA}`
                              : c.creeeLe
                                ? `reçue à ${new Date(
                                    c.creeeLe,
                                  ).toLocaleString("fr-FR")}`
                                : "heure inconnue"}
                          </span>
                          {/* QUI (01/11/2026) : le responsable de la
                              commande, et celui qui a demande le retrait
                              dans l'application quand il y en a un. */}
                          <div className="mt-1 text-xs text-gray-700">
                            👤 Responsable commande :{" "}
                            <strong>{(c as any).responsable || "non attribuée"}</strong>
                            {Array.isArray((c as any).retraitsDemandes) &&
                              (c as any).retraitsDemandes.length > 0 && (
                                <span className="text-gray-500">
                                  {" "}· retrait demandé par{" "}
                                  {Array.from(
                                    new Set(
                                      (c as any).retraitsDemandes
                                        .map((r: any) => r.par)
                                        .filter(Boolean),
                                    ),
                                  ).join(", ") || "?"}
                                </span>
                              )}
                          </div>
                        </div>
                        {tropHaut ? (
                          <span className="text-emerald-700 font-bold tabular-nums">
                            +{formatPriceCompact(ecartHaut)} MAD
                          </span>
                        ) : (
                          <span className="text-red-600 font-bold tabular-nums">
                            −{formatPriceCompact(c.perduCentimes)} MAD
                          </span>
                        )}
                      </div>

                      {tropHaut ? (
                        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5">
                          <p className="text-sm font-semibold text-emerald-800">
                            Validée au-dessus du montant commandé
                          </p>
                          <p className="text-xs text-emerald-700 mt-1">
                            Glovo a encaissé {formatPriceCompact(ecartHaut)} MAD de plus
                            que le montant commandé (montant validé :{" "}
                            {formatPriceCompact(c.montantFactureCentimes)} MAD). Souvent un
                            prix saisi trop haut à la validation, ou le prix d&apos;une
                            pesée. À vérifier avec le responsable.
                          </p>
                        </div>
                      ) : c.articles.length > 0 ? (
                        <ul className="space-y-1">
                          {c.articles.map((a: any, i: number) => (
                            <li
                              key={i}
                              className="flex justify-between text-sm text-gray-700 gap-4"
                            >
                              <span className="min-w-0">
                                <span className="text-gray-400 mr-1.5 tabular-nums">
                                  {a.quantite}×
                                </span>
                                {a.nom}
                              </span>
                              <span className="tabular-nums text-gray-500 shrink-0">
                                {formatPriceCompact(
                                  Number(a.prixCentimes || 0) *
                                    Number(a.quantite || 1),
                                )}{" "}
                                MAD
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        c.identificationImpossible ? (
                          // Les articles absents du fichier Glovo coutaient
                          // plus cher que la perte : impossible que ce soient
                          // eux. On le dit, plutot que de nommer au hasard.
                          <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5">
                            <p className="text-sm font-semibold text-amber-900">
                              Articles non identifiés
                            </p>
                            <p className="text-xs text-amber-800 mt-1">
                              La perte de{" "}
                              {formatPriceCompact(c.perduCentimes)} MAD est
                              exacte, mais les articles manquants chez Glovo
                              coûtent plus cher que cette perte : ce ne peut
                              pas être eux. Soit un nom de produit ne
                              correspond pas, soit le montant a été mal validé.
                              À ouvrir chez Glovo.
                            </p>
                          </div>
                        ) : (
                        <div className="rounded-md bg-red-50 border border-red-200 p-2.5">
                          <p className="text-sm font-semibold text-red-800">
                            ⚠️ Erreur de validation probable
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            Aucun article n'a été retiré : le montant validé est
                            inférieur de {formatPriceCompact(c.perduCentimes)}{" "}
                            MAD au montant commandé. À vérifier avec le
                            responsable.
                          </p>
                        </div>
                        )
                      )}

                      {/* Desormais la liste ne peut plus depasser la perte :
                          l'ecart restant est toujours du manque, jamais du
                          trop-plein. C'est donc une erreur de validation, et
                          on l'appelle par son nom. */}
                      {c.articles.length > 0 && ecart > 50 && (
                        <p className="mt-2 text-xs text-orange-700">
                          {formatPriceCompact(ecart)} MAD de la perte ne
                          correspondent à aucun article retiré : le montant a
                          été validé trop bas.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Erreurs de validation — montant validé sous le montant commandé,
            alors qu'aucun article n'a été retiré. Carte distincte des ventes
            perdues : la cause est humaine, pas logistique, et le remède
            n'est pas le même. */}
        <button
          type="button"
          disabled={erreursValidation.length === 0}
          onClick={() =>
            setDetailPerdues({
              ...resumePerdues,
              commandes: erreursValidation,
              total: totalValidation,
              titre: "Erreurs de validation",
            })
          }
          className={`text-left bg-white rounded-xl p-4 border shadow-sm transition-colors ${
            erreursValidation.length === 0
              ? "border-gray-200 cursor-default"
              : soldeValidation >= 0
                ? "border-green-300 hover:bg-green-50/50 cursor-pointer"
                : "border-red-300 hover:bg-red-50/50 cursor-pointer"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Erreurs de validation
              </p>
              <p
                className={`text-2xl md:text-3xl font-bold mt-1 tabular-nums ${
                  erreursValidation.length === 0
                    ? "text-gray-400"
                    : soldeValidation >= 0
                      ? "text-green-600"
                      : "text-red-600"
                }`}
              >
                {resumePerdues === null
                  ? "—"
                  : /* Le signe se lit avant le chiffre : on doit savoir
                       si la maison a gagne ou perdu sans faire un calcul. */
                    `${soldeValidation > 0 ? "+" : soldeValidation < 0 ? "−" : ""}${formatPriceCompact(
                      Math.abs(soldeValidation),
                    )}`}
              </p>
              {/* Le detail sous le solde : un solde seul cache d'ou il
                  vient. 25 de perte nette peut etre 25 en moins, ou 30 en
                  moins et 5 en trop — ce ne sont pas les memes erreurs. */}
              <p className="text-gray-400 text-xs mt-1.5 leading-snug">
                {erreursValidation.length === 0
                  ? "aucune erreur"
                  : [
                      validationEnMoins > 0
                        ? `${formatPriceCompact(validationEnMoins)} en moins`
                        : null,
                      validationEnTrop > 0
                        ? `${formatPriceCompact(validationEnTrop)} en trop`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") +
                    ` — ${erreursValidation.length} commande${
                      erreursValidation.length > 1 ? "s" : ""
                    }`}
              </p>
            </div>
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                erreursValidation.length === 0
                  ? "bg-gray-100"
                  : soldeValidation >= 0
                    ? "bg-green-100"
                    : "bg-red-100"
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 ${
                  erreursValidation.length === 0
                    ? "text-gray-400"
                    : soldeValidation >= 0
                      ? "text-green-600"
                      : "text-red-600"
                }`}
              />
            </div>
          </div>
        </button>

        {/* Déductions — commission et frais opérationnels retenus par Glovo.
            Cliquable : un chiffre qu'on ne peut pas ouvrir est un chiffre
            qu'on finit par ne plus croire. */}
        <button
          type="button"
          disabled={refunds <= 0}
          onClick={async () => {
            setChargementDeductions(true);
            try {
              const r = await api.orders.getRemboursements({
                storeId: storeFilter !== "ALL" ? storeFilter : undefined,
                dateFilter,
              });
              setPhotosOuvertes({});
              setDetailDeductions(r);
            } catch (e: any) {
              alert(`Impossible de charger le détail : ${e.message}`);
            } finally {
              setChargementDeductions(false);
            }
          }}
          className={`text-left bg-white rounded-xl p-4 border border-gray-200 shadow-sm transition-colors ${
            refunds > 0
              ? "hover:border-orange-300 hover:bg-orange-50/40 cursor-pointer"
              : "cursor-default"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Remboursements
              </p>
              <p className="text-2xl md:text-3xl font-bold text-orange-600 mt-1 tabular-nums">
                {chargementDeductions ? "…" : formatPriceCompact(refunds)}
              </p>
              <p className="text-gray-400 text-xs mt-1.5 leading-snug">
                {refunds > 0
                  ? "retenus par Glovo — voir le détail et les photos"
                  : "aucune retenue"}
              </p>
              {/* LE CUMUL DU MOIS, SOUS LE CHIFFRE DU JOUR.
                  Les deux se lisent ensemble ou ne se lisent pas : sans le
                  cumul, une journée calme laisse croire que le mois l'est
                  aussi. */}
              {typeof refundsMois === "number" && refundsMois > 0 && (
                <p className="text-orange-700 text-xs font-semibold mt-1 tabular-nums">
                  {formatPriceCompact(refundsMois)} MAD depuis le 1er du mois
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
              <Receipt className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </button>

        {detailDeductions && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setDetailDeductions(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Remboursements — {libellePeriode(dateFilter)}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {detailDeductions.commandes.length} commande
                    {detailDeductions.commandes.length > 1 ? "s" : ""} ·{" "}
                    <span className="font-semibold text-orange-600 tabular-nums">
                      {formatPriceCompact(detailDeductions.total)} MAD
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => setDetailDeductions(null)}
                  className="p-1 rounded hover:bg-gray-100"
                  aria-label="Fermer"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {detailDeductions.commandes.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Aucune retenue sur cette période.
                  </p>
                )}

                {detailDeductions.commandes.map((c: any) => (
                  <div
                    key={c.id}
                    className="rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-900">#{c.code}</p>
                        <p className="text-xs text-gray-400">
                          {c.magasin} · {c.recueA || "date inconnue"}
                        </p>
                      </div>
                      <p className="font-bold text-orange-600 tabular-nums">
                        − {formatPriceCompact(c.montantCentimes)} MAD
                      </p>
                    </div>

                    {c.justificatifs.length === 0 ? (
                      <p className="text-xs font-semibold text-red-600 mt-2">
                        aucune preuve — à justifier depuis Import Glovo
                      </p>
                    ) : (
                      <div className="mt-2 space-y-1">
                        {c.justificatifs.map((j: any) => (
                          <p key={j.id} className="text-sm text-gray-700">
                            {j.article || "article non précisé"}
                            {j.motif ? ` — ${j.motif}` : ""}
                          </p>
                        ))}

                        {/* Les photos ne descendent que sur demande : une
                            photo pese 300 Ko, et cette fenetre s'ouvre depuis
                            le magasin, en 4G. */}
                        <button
                          type="button"
                          onClick={async () => {
                            if (photosOuvertes[c.id]) {
                              setPhotosOuvertes((p) => {
                                const s = { ...p };
                                delete s[c.id];
                                return s;
                              });
                              return;
                            }
                            try {
                              const r = await api.importGlovo.justificatifs(c.id);
                              setPhotosOuvertes((p) => ({ ...p, [c.id]: r.photos }));
                            } catch (e: any) {
                              alert(`Photos indisponibles : ${e.message}`);
                            }
                          }}
                          className="text-xs font-semibold text-blue-600 hover:underline"
                        >
                          {photosOuvertes[c.id]
                            ? "masquer les photos"
                            : `voir les ${c.justificatifs.length} photo${
                                c.justificatifs.length > 1 ? "s" : ""
                              }`}
                        </button>

                        {photosOuvertes[c.id] && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {photosOuvertes[c.id].map((p: any) => (
                              <img
                                key={p.id}
                                src={p.photo}
                                alt={p.article || "justificatif"}
                                className="w-28 h-28 object-cover rounded-lg border border-gray-200"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {detailDeductions.tronquee && (
                  <p className="text-xs text-orange-700 bg-orange-50 rounded p-2">
                    Période trop large : seules les 500 commandes les plus
                    récentes ont été examinées.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* En attente */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                En attente
              </p>
              <p className="text-2xl md:text-3xl font-bold text-yellow-600 mt-1">
                {pendingCount}
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* En cours */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                En cours
              </p>
              <p className="text-2xl md:text-3xl font-bold text-purple-600 mt-1">
                {inProgressCount + readyCount}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Livrées / Annulées */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm col-span-2 md:col-span-2 2xl:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Livrées
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-700 mt-1">
                {deliveredCount}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1 text-red-500">
                <Ban className="w-4 h-4" />
                <span className="text-sm font-semibold">{cancelledCount}</span>
              </div>
              <span className="text-xs text-gray-400">annulées</span>
              {annulations?.montantCentimes != null && (
                <span className="text-sm font-semibold text-red-600 tabular-nums">
                  {formatPriceCompact(annulations.montantCentimes)} DH
                </span>
              )}
            </div>
          </div>
          {/* PAR RESPONSABLE COMMANDE (02/11/2026) : combien, combien
              d'argent, et qui a annule (Glovo, ou quelqu'un de chez nous). */}
          {Array.isArray(annulations?.parResponsable) && annulations.parResponsable.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <button
                onClick={() => setAnnulationsOuvertes((v) => !v)}
                className="text-xs text-red-700 hover:underline"
              >
                {annulationsOuvertes ? "Masquer" : "Voir"} par responsable
              </button>
              {annulationsOuvertes && (
                <ul className="mt-1 space-y-1.5">
                  {annulations.parResponsable.map((g: any) => (
                    <li key={g.nom} className="text-xs text-gray-700">
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold truncate">👤 {g.nom}</span>
                        <span className="tabular-nums whitespace-nowrap">
                          {g.nombre}
                          {g.montantCentimes != null && (
                            <b className="text-red-600"> · {formatPriceCompact(g.montantCentimes)} DH</b>
                          )}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {[
                          g.parGlovo ? `${g.parGlovo} par Glovo` : "",
                          ...Object.entries(g.par || {}).map(
                            ([qui, n]) => `${n} par ${qui === "inconnu" ? "nous (nom inconnu)" : qui}`,
                          ),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        {g.payeesCentimes ? ` · dont ${formatPriceCompact(g.payeesCentimes)} DH payés par Glovo` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Moyenne de preparation.
            Le nombre de commandes retenues est affiche dessous : une moyenne
            sur deux commandes ne vaut pas une moyenne sur cinquante, et
            l'ecran doit le dire plutot que de laisser croire au contraire. */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Préparation
              </p>
              <p
                className={`text-2xl md:text-3xl font-bold mt-1 tabular-nums ${
                  preparationMoyenne && preparationMoyenne.minutes >= 15
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {preparationMoyenne ? `${preparationMoyenne.minutes} min` : "—"}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {preparationMoyenne
                  ? `moyenne sur ${preparationMoyenne.surCombien} commande${
                      preparationMoyenne.surCombien > 1 ? "s" : ""
                    }`
                  : "aucune commande terminée"}
              </p>
            </div>
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-teal-600" />
            </div>
          </div>
        </div>

        {/* LE COUT D'UNE COMMANDE — la direction seule.
            Le serveur ne l'envoie qu'a l'administrateur : il repose sur un
            salaire. La tuile n'existe donc tout simplement pas chez le
            responsable commande ou le support — il n'y a rien a cacher. */}
        {(stats as any)?.coutCommande && (() => {
          const c = (stats as any).coutCommande;
          const dh = (centimes: number) =>
            (centimes / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          const unSeulMagasin = storeFilter !== "ALL";
          return (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                    Coût d&apos;une commande
                  </p>
                  <p className="text-2xl md:text-3xl font-bold mt-1 tabular-nums text-slate-800">
                    {c.coutParCommandeCentimes !== null
                      ? `${dh(c.coutParCommandeCentimes)} DH`
                      : "—"}
                  </p>
                  {/* LE CALCUL, EN CLAIR. Un chiffre qu'on ne peut pas
                      refaire de tete ne se discute pas, il se subit. */}
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                    {dh(c.salaireMensuelCentimes)} DH ÷ {c.joursOuvresDuMois ?? "—"} j
                    {" "}= {c.coutJourCentimes !== null ? dh(c.coutJourCentimes) : "—"} DH/j
                    <br />
                    {c.joursPayes} j payé{c.joursPayes > 1 ? "s" : ""} ÷{" "}
                    {c.commandes} commande{c.commandes > 1 ? "s" : ""}
                  </p>

                  {/* Le salaire se regle magasin par magasin : c'est a UN
                      responsable qu'on le verse. Sur « tous les stores », on
                      montre la somme, sans la proposer a la modification. */}
                  {unSeulMagasin && salaireSaisi === null && (
                    <button
                      type="button"
                      onClick={() =>
                        setSalaireSaisi(String(c.salaireMensuelCentimes / 100))
                      }
                      className="text-[11px] font-semibold text-blue-600 hover:underline mt-1"
                    >
                      modifier le salaire
                    </button>
                  )}
                  {unSeulMagasin && salaireSaisi !== null && (
                    <div className="flex items-center gap-1 mt-1">
                      <input
                        value={salaireSaisi}
                        onChange={(e) => setSalaireSaisi(e.target.value)}
                        inputMode="decimal"
                        className="w-20 text-xs border border-gray-200 rounded px-1.5 py-1"
                      />
                      <button
                        type="button"
                        disabled={salaireEnvoi}
                        onClick={async () => {
                          const m = Number(String(salaireSaisi).replace(",", "."));
                          if (!Number.isFinite(m) || m < 0) return;
                          setSalaireEnvoi(true);
                          try {
                            await api.stores.salaireResponsable(storeFilter, m);
                            setSalaireSaisi(null);
                            await queryClient.invalidateQueries();
                          } catch {
                            /* La valeur reste a l'ecran : on peut reessayer. */
                          } finally {
                            setSalaireEnvoi(false);
                          }
                        }}
                        className="text-[11px] font-bold text-white bg-slate-800 rounded px-2 py-1 disabled:opacity-50"
                      >
                        {salaireEnvoi ? "…" : "OK"}
                      </button>
                    </div>
                  )}

                  {/* Le detail, poste par poste. Un cout global ne dit pas
                      ou agir ; la ligne « IA : 1 200 DH » si. */}
                  {Array.isArray(c.detail) && c.detail.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setDetailCharges((v) => !v)}
                      className="text-[11px] font-semibold text-blue-600 hover:underline mt-1 block"
                    >
                      {detailCharges
                        ? "masquer le détail"
                        : `voir le détail (${c.detail.length} postes)`}
                    </button>
                  )}
                </div>
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                  <Coins className="w-5 h-5 text-slate-600" />
                </div>
              </div>

              {detailCharges && Array.isArray(c.detail) && (() => {
                // DEUX NATURES DE DEPENSE, DEUX LEVIERS DIFFERENTS.
                //
                // Un salaire ne se negocie pas comme un abonnement : il se
                // rentabilise en faisant plus de commandes avec la meme
                // equipe. Un outil, lui, se change ou se resilie. Les melanger
                // dans une seule liste empechait de voir lequel des deux
                // pese, et donc sur lequel agir.
                const salaires = c.detail.filter(
                  (p: any) => p.categorie === "SALAIRE",
                );
                const outils = c.detail.filter(
                  (p: any) => p.categorie !== "SALAIRE",
                );
                const somme = (l: any[], champ: string) =>
                  l.reduce((t, p) => t + (p[champ] || 0), 0);

                const ligne = (p: any, i: number) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="py-1 pr-2 text-gray-700 pl-2">
                      {p.libelle}
                      {p.categorie !== "SALAIRE" && (
                        <span className="ml-1 text-[9px] uppercase tracking-wide text-gray-400">
                          {p.categorie}
                        </span>
                      )}
                    </td>
                    <td className="py-1 text-right tabular-nums text-gray-700">
                      {dh(p.montantMensuelCentimes)}
                    </td>
                    <td className="py-1 text-right tabular-nums font-semibold text-slate-800">
                      {p.partParCommandeCentimes !== null
                        ? dh(p.partParCommandeCentimes)
                        : "—"}
                    </td>
                    <td className="py-1 text-right tabular-nums text-gray-400">
                      {p.partPourcent} %
                    </td>
                  </tr>
                );

                const sousTotal = (
                  titre: string,
                  liste: any[],
                  couleur: string,
                ) => (
                  <tr className={`border-t border-gray-200 font-semibold ${couleur}`}>
                    <td className="py-1">{titre}</td>
                    <td className="py-1 text-right tabular-nums">
                      {dh(somme(liste, "montantMensuelCentimes"))}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {dh(somme(liste, "partParCommandeCentimes"))}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {somme(liste, "partPourcent").toFixed(1)} %
                    </td>
                  </tr>
                );

                return (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <table className="w-full text-[11px]">
                      <thead className="text-gray-400">
                        <tr>
                          <th className="text-left font-medium pb-1">Poste</th>
                          <th className="text-right font-medium pb-1">Par mois</th>
                          <th className="text-right font-medium pb-1">Par cde</th>
                          <th className="text-right font-medium pb-1">Part</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salaires.length > 0 && (
                          <>
                            <tr className="bg-amber-50">
                              <td
                                colSpan={4}
                                className="py-1 px-1 text-[10px] font-bold uppercase tracking-wide text-amber-800"
                              >
                                Salaires
                              </td>
                            </tr>
                            {salaires.map(ligne)}
                            {sousTotal("Sous-total salaires", salaires, "text-amber-800")}
                          </>
                        )}

                        {outils.length > 0 && (
                          <>
                            <tr className="bg-sky-50">
                              <td
                                colSpan={4}
                                className="py-1 px-1 text-[10px] font-bold uppercase tracking-wide text-sky-800 pt-2"
                              >
                                Autres charges (outils, services)
                              </td>
                            </tr>
                            {outils.map(ligne)}
                            {sousTotal("Sous-total autres charges", outils, "text-sky-800")}
                          </>
                        )}

                        <tr className="border-t-2 border-gray-300 font-bold text-slate-800">
                          <td className="py-1.5">Total</td>
                          <td className="py-1.5 text-right tabular-nums">
                            {dh(c.salaireMensuelCentimes)}
                          </td>
                          <td className="py-1.5 text-right tabular-nums">
                            {c.coutParCommandeCentimes !== null
                              ? dh(c.coutParCommandeCentimes)
                              : "—"}
                          </td>
                          <td className="py-1.5 text-right text-gray-400">100 %</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 mt-2 leading-snug">
                      Montants en DH. Les salaires se règlent magasin par
                      magasin&nbsp;; les autres charges dans Paramètres →
                      Charges mensuelles.
                    </p>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </div>

      {/* Fraicheur des chiffres venant du fichier Glovo */}
      <div className="flex items-center gap-2 text-xs text-gray-500 -mt-1">
        <FileSpreadsheet className="w-3.5 h-3.5 shrink-0" />
        {dernierImport?.quand ? (
          <span>
            Dernier import Glovo&nbsp;:{" "}
            <span className="font-medium text-gray-700">
              {new Date(dernierImport.quand).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: FUSEAU_MAROC,
              })}
            </span>{" "}
            · {dernierImport.commandesLues} commande
            {dernierImport.commandesLues > 1 ? "s" : ""} lue
            {dernierImport.commandesLues > 1 ? "s" : ""}
          </span>
        ) : (
          <span>
            Aucun fichier Glovo importé — ventes perdues, déductions et montants
            nets restent à zéro tant qu'aucun import n'a eu lieu.
          </span>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
            <p className="text-sm text-gray-600 mt-1">
              {totalOrders} commande{totalOrders > 1 ? "s" : ""} trouvée
              {totalOrders > 1 ? "s" : ""}
              {totalPages > 1 && ` (page ${currentPage}/${totalPages})`}
            </p>
          </div>

          {/* L'EXPORT EXCEL — une ligne par article, avec les filtres
              ci-dessous. Admin seulement : le fichier porte les prix
              d'achat, les fournisseurs et les marges. */}
          {(user?.role === UserRole.ADMIN || user?.role === UserRole.SUPPORT) && (
            <button
              type="button"
              disabled={exportEnCours}
              onClick={async () => {
                setExportEnCours(true);
                try {
                  const nom = await api.analytique.exporterCommandes({
                    dateFilter,
                    storeId: storeFilter,
                    source: plateformeFilter,
                    status: statusFilter,
                  });
                  showToast(`Fichier téléchargé : ${nom}`, "success");
                } catch (e: any) {
                  showToast(e?.message || "Export impossible", "error");
                } finally {
                  setExportEnCours(false);
                }
              }}
              title={`Exporter en Excel — ${libellePeriode(dateFilter)}`}
              className="inline-flex items-center justify-center gap-2 self-start md:self-auto px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exportEnCours ? "Préparation du fichier…" : "Exporter Excel"}
            </button>
          )}
        </div>

        {/* LE CADRE FILTRES — pas pour le responsable commande.
            Il travaille sur la liste telle qu'elle arrive. Le menu « Tous
            les stores » ne voulait de toute facon plus rien dire depuis
            qu'il est limite a son magasin.

            On cache le CADRE, on ne debranche pas le mecanisme : les
            filtres continuent de decider ce que la liste demande au
            serveur, avec leurs valeurs par defaut. */}
        {!estCollaborateur && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres</span>
              {/* 🔥 Badge si filtre téléphone actif */}
              {phoneFilter && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                  📞 {phoneFilter}
                </span>
              )}
            </div>

            {codeFilter && (
              <p className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Recherche par numéro : la période, le magasin et la plateforme
                sélectionnés sont ignorés, pour retrouver la commande où qu'elle
                soit.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Numéro de commande */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Numéro de commande
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={codeSaisi}
                    onChange={(e) => setCodeSaisi(e.target.value)}
                    placeholder="101760509383"
                    className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary tabular-nums"
                  />
                  {codeSaisi && (
                    <button
                      onClick={() => setCodeSaisi("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Effacer"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filtre par statut */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Statut
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as OrderStatus | "ALL")
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="ALL">Tous les statuts</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtre par plateforme */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Plateforme
                </label>
                <select
                  value={plateformeFilter}
                  onChange={(e) => setPlateformeFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  {/* TES TROIS SOURCES DE REVENU.
                      Le site manquait alors que ses commandes existent bien :
                      le webhook POST /api/site/wix les cree avec la source
                      WEBSITE, et elles tombaient dans « Glovo + Kooul » sans
                      qu'on puisse les isoler. Le libelle mentait aussi : ALL
                      n'a jamais filtre, il montre TOUT. */}
                  <option value="ALL">Toutes les sources</option>
                  <option value="GLOVO">Glovo</option>
                  <option value="WEBSITE">Site — naturabeldi.ma</option>
                  <option value="KOOUL">Kooul</option>
                </select>
              </div>

              {/* Filtre par store */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Store
                </label>
                <select
                  value={storeFilter}
                  onChange={(e) => setStoreFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="ALL">Tous les stores</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtre par date */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Période
                </label>
                <div className="relative">
                  {/* Le bouton porte la periode active : l'ecran ne montre
                      plus cinq boutons inutiles a cote d'un seul utile. */}
                  <button
                    onClick={() => {
                      setDebutPlage(null);
                      setPeriodeOuverte((v) => !v);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex items-center gap-2 text-gray-800">
                      <CalendarDays className="w-4 h-4 text-gray-500" />
                      {libellePeriode(dateFilter)}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform ${
                        periodeOuverte ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {periodeOuverte && (
                    <>
                      {/* Zone transparente : un clic a cote referme le
                          panneau, comme on s'y attend. */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setPeriodeOuverte(false)}
                      />
                      <div className="absolute z-20 mt-2 right-0 w-[19rem] bg-white rounded-xl shadow-xl border border-gray-200 p-4">
                        <p className="text-xs font-semibold text-primary mb-3">
                          Sélectionnez une période
                        </p>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {(
                            [
                              ["today", "Aujourd'hui"],
                              ["yesterday", "Hier"],
                              ["7days", "7 jours"],
                              ["15days", "15 jours"],
                              ["30days", "30 jours"],
                              ["all", "Tout"],
                            ] as [DateFilter, string][]
                          ).map(([valeur, texte]) => (
                            <button
                              key={valeur}
                              onClick={() => {
                                setDateFilter(valeur);
                                setPeriodeOuverte(false);
                              }}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                dateFilter === valeur
                                  ? "bg-primary text-white border-primary"
                                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {texte}
                            </button>
                          ))}
                        </div>

                        {/* Calendrier — un jour, ou « du … au … » :
                            premier clic = debut, second clic = fin. */}
                        <p className="text-[11px] text-gray-500 mb-2">
                          {debutPlage
                            ? "Choisissez maintenant le dernier jour."
                            : "Un jour, ou « du … au … » : cliquez le premier jour, puis le dernier."}
                        </p>
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={() =>
                              setMoisAffiche((m) =>
                                m.mois === 0
                                  ? { annee: m.annee - 1, mois: 11 }
                                  : { ...m, mois: m.mois - 1 },
                              )
                            }
                            className="p-1 rounded hover:bg-gray-100"
                            aria-label="Mois précédent"
                          >
                            <ChevronLeft className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="text-sm font-medium text-gray-800">
                            {new Date(
                              moisAffiche.annee,
                              moisAffiche.mois,
                              1,
                            ).toLocaleDateString("fr-FR", {
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <button
                            onClick={() =>
                              setMoisAffiche((m) =>
                                m.mois === 11
                                  ? { annee: m.annee + 1, mois: 0 }
                                  : { ...m, mois: m.mois + 1 },
                              )
                            }
                            className="p-1 rounded hover:bg-gray-100"
                            aria-label="Mois suivant"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>

                        <div className="grid grid-cols-7 gap-1 mb-1">
                          {["L", "M", "M", "J", "V", "S", "D"].map((j, i) => (
                            <div
                              key={i}
                              className="text-center text-[11px] text-gray-400"
                            >
                              {j}
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                          {grilleDuMois(moisAffiche.annee, moisAffiche.mois).map(
                            (jour, i) => {
                              if (jour === null)
                                return <div key={`v${i}`} className="h-8" />;

                              const iso = `${moisAffiche.annee}-${String(
                                moisAffiche.mois + 1,
                              ).padStart(2, "0")}-${String(jour).padStart(
                                2,
                                "0",
                              )}`;
                              // Les bornes de la periode affichee : un jour
                              // seul, ou une plage (les deux jours inclus).
                              const [pDebut, pFin] = debutPlage
                                ? [debutPlage, debutPlage]
                                : dateFilter.startsWith("plage:")
                                  ? dateFilter.slice(6).split("_")
                                  : dateFilter.startsWith("jour:")
                                    ? [dateFilter.slice(5), dateFilter.slice(5)]
                                    : ["", ""];
                              const choisi = iso === pDebut || iso === pFin;
                              const dedans = !choisi && pDebut !== "" && iso > pDebut && iso < pFin;
                              // Une date future n'a aucune commande : on la
                              // laisse visible mais inactive plutot que de
                              // renvoyer un ecran vide sans explication.
                              const futur =
                                new Date(`${iso}T12:00:00`).getTime() >
                                Date.now();

                              return (
                                <button
                                  key={iso}
                                  disabled={futur}
                                  onClick={() => {
                                    // 1er clic : le debut. La liste montre
                                    // deja ce jour, au cas ou on s'arrete la.
                                    if (!debutPlage || iso < debutPlage) {
                                      setDebutPlage(iso);
                                      setDateFilter(`jour:${iso}`);
                                      return;
                                    }
                                    // 2e clic : la fin (le meme jour = ce jour seul).
                                    setDateFilter(
                                      iso === debutPlage
                                        ? `jour:${iso}`
                                        : `plage:${debutPlage}_${iso}`,
                                    );
                                    setDebutPlage(null);
                                    setPeriodeOuverte(false);
                                  }}
                                  className={`h-8 text-xs rounded-full transition-colors ${
                                    choisi
                                      ? "bg-primary text-white font-semibold"
                                      : dedans
                                        ? "bg-primary/15 text-gray-900"
                                        : futur
                                        ? "text-gray-300 cursor-not-allowed"
                                        : "text-gray-700 hover:bg-gray-100"
                                  }`}
                                >
                                  {jour}
                                </button>
                              );
                            },
                          )}
                        </div>

                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setDebutPlage(null);
                              setDateFilter("all");
                              setPeriodeOuverte(false);
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Réinitialiser
                          </button>
                          <button
                            onClick={() => {
                              setDebutPlage(null);
                              setPeriodeOuverte(false);
                            }}
                            className="px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
                          >
                            Fermer
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* PERIODES CLOTUREES (02/11/2026), meme principe que la caisse :
                  une quinzaine cloturee est calculee une fois, puis lue en
                  une seule lecture. */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Périodes clôturées
                </label>
                <select
                  value={quinzaineCourante ? quinzaineCourante.filtre : ""}
                  onChange={(e) => {
                    if (e.target.value) setDateFilter(e.target.value as DateFilter);
                  }}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white"
                >
                  <option value="">Choisir une quinzaine…</option>
                  {quinzaines
                    .filter((q) => q.terminee)
                    .map((q) => (
                      <option key={q.cle} value={q.filtre}>
                        {q.cloturee ? "🔒 " : ""}
                        {libellePeriode(q.filtre)}
                        {q.cloturee ? " — clôturée" : ""}
                      </option>
                    ))}
                </select>
                {quinzaineCourante && quinzaineCourante.terminee && (
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    {quinzaineCourante.cloturee ? (
                      <span className="font-semibold text-emerald-700">🔒 Clôturée : chiffres figés</span>
                    ) : (
                      <span className="text-gray-500">Non clôturée</span>
                    )}
                    {user?.role === UserRole.ADMIN && (
                      <button
                        disabled={clotureEnCours}
                        onClick={async () => {
                          const cloturer = !quinzaineCourante.cloturee;
                          setClotureEnCours(true);
                          try {
                            await api.orders.cloturer(quinzaineCourante.cle, cloturer);
                            showToast(cloturer ? "Quinzaine clôturée" : "Quinzaine rouverte", "success");
                            chargerQuinzaines();
                            queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                          } catch (e: any) {
                            showToast(e?.message || "Clôture impossible", "error");
                          } finally {
                            setClotureEnCours(false);
                          }
                        }}
                        className="px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {clotureEnCours ? "…" : quinzaineCourante.cloturee ? "Rouvrir" : "Clôturer"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Liste des commandes */}
      {orders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune commande trouvée</p>
        </div>
      ) : (
        <>
          {/* Vue mobile : Cartes */}
          <div className="md:hidden space-y-3">
            {orders.map((order: any) => (
              <div
                key={order.id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      {(order as any).orderCode ||
                        order.externalId ||
                        order.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate((order as any).orderTime || order.createdAt)}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      STATUS_COLORS[order.status as OrderStatus] ||
                      "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {STATUS_LABELS[order.status as OrderStatus] || order.status}
                  </span>
                </div>

                <div className="space-y-2 mb-3">
                  <div>
                    <div className="text-xs text-gray-500">Client</div>
                    <div className="text-sm font-medium text-gray-900">
                      {order.customerName}
                    </div>
                    {/* Pas de ligne telephone pour le responsable : le
                        serveur ne lui envoie plus le numero, et une case
                        vide invite a se demander ce qui manque. */}
                    {!estCollaborateur && (
                      <CustomerPhoneCell
                        order={order}
                        canEdit={canEditPhone}
                        onSaved={handlePhoneSaved}
                        onError={(m) => showToast(m, "error")}
                      />
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Store</div>
                    <div className="text-sm text-gray-900">
                      {(order as any).store?.name || "—"}
                    </div>
                  </div>
                  {/* Le temps de preparation. Rouge au-dela de 20 minutes :
                      un chiffre qui ne change jamais de couleur finit par ne
                      plus etre lu. */}
                  {(() => {
                    const t = tempsPreparation(order);
                    if (t.enAttente || t.minutes === null) {
                      // Le fichier Glovo ne concerne que les commandes Glovo.
                      if (nomSource(order.source) !== "GLOVO") return null;
                      return (
                        <div>
                          <div className="text-xs text-gray-500">Préparation</div>
                          <div className="text-sm text-gray-400">
                            connue après l&apos;import du fichier Glovo
                          </div>
                        </div>
                      );
                    }
                    // Le meme seuil que dans le tableau. Deux ecrans qui
                    // colorient le meme chiffre differemment, c'est ainsi
                    // qu'on finit par ne plus croire ni l'un ni l'autre.
                    const dansLesTemps = t.minutes < 15;
                    return (
                      <div>
                        <div className="text-xs text-gray-500">Préparation</div>
                        <div
                          className={`text-sm font-semibold tabular-nums flex items-center gap-1.5 ${
                            dansLesTemps ? "text-green-700" : "text-red-700"
                          }`}
                        >
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          {`préparée en ${t.minutes} min`}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between items-center">
                    <div className={voitPrix ? "" : "hidden"}>
                      <div className="text-xs text-gray-500">Montant</div>
                      {(() => {
                        const g = infosGlovo(order);
                        return (
                          <>
                            <div className="text-sm font-semibold text-gray-900">
                              {formatPrice(g.net ?? order.totalAmount)}
                              {g.net !== null && (
                                <span className="text-[11px] font-normal text-gray-400 ml-1">
                                  net
                                </span>
                              )}
                            </div>
                            {g.remboursement > 0 && (
                              <div className="text-[11px] font-medium text-red-600 leading-tight">
                                − {formatPrice(g.remboursement)} remboursé
                              </div>
                            )}
                            {g.preparationMinutes !== null && (
                              <div className="text-[11px] text-gray-500 leading-tight">
                                préparée en {g.preparationMinutes} min
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col items-end">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${couleurSource(order.source)}`}
                      >
                        {nomSource(order.source)}
                      </span>
                      <BadgePaiement order={order} />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-2">
                  {canEditPhone && estAnnulable(order) && (
                    <button
                      onClick={() => setAnnulerDepuisListe(order)}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Annuler
                    </button>
                  )}
                  {!canEditPhone && canManageOrders && order.status === OrderStatus.PENDING && (
                    <>
                      <button
                        onClick={() => handleCancel(order.id)}
                        disabled={processing === order.id}
                        className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Annuler
                      </button>
                    </>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        console.log("Loading order details for:", order.id);
                        const fullOrder = await api.orders.getById(order.id);
                        console.log("Order details loaded:", fullOrder);
                        setSelectedOrder(fullOrder);
                      } catch (error) {
                        console.error("Error loading order details:", error);
                        setSelectedOrder(order);
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Détails
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Vue desktop : Tableau */}
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Store
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    {voitPrix && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Montant
                    </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order: any) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(order as any).orderCode ||
                            order.externalId ||
                            order.id.slice(0, 8)}
                        </div>
                        {order.externalId && (
                          <div className="text-xs text-gray-500">
                            ID: {order.externalId}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customerName}
                        </div>
                        <CustomerPhoneCell
                          order={order}
                          canEdit={canEditPhone}
                          onSaved={handlePhoneSaved}
                          onError={(m) => showToast(m, "error")}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {(order as any).store?.name || "—"}
                        </div>
                        {/* L'identifiant Glovo du magasin n'a de sens que pour
                            une commande Glovo (10/10/2026). */}
                        {(order as any).store?.glovoStoreId &&
                          nomSource(order.source) === "GLOVO" && (
                          <div className="text-xs text-gray-500">
                            Glovo: {(order as any).store.glovoStoreId}
                          </div>
                        )}
                        {/* LE RESPONSABLE COMMANDE QUI S'EN EST OCCUPE (21/10/2026). */}
                        {(order as any).responsableNom && (
                          <div className="mt-0.5 text-xs font-semibold text-indigo-700">
                            👤 {(order as any).responsableNom}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${couleurSource(order.source)}`}
                    >
                      {nomSource(order.source)}
                    </span>
                      </td>
                      {voitPrix && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {(() => {
                          const g = infosGlovo(order);
                          return (
                            <>
                              <div
                                className={`font-medium ${
                                  g.net === 0 ? "text-gray-400" : "text-gray-900"
                                }`}
                              >
                                {formatPrice(g.net ?? order.totalAmount)}
                              </div>
                              {g.net !== null && (
                                <div className="text-[11px] text-gray-400 leading-tight">
                                  {g.net === 0 ? "rien à recevoir" : "net à recevoir"}
                                </div>
                              )}
                              {/* LE REMBOURSEMENT, SOUS LE NET, EN ROUGE.
                                  Assez gros pour se voir en parcourant la
                                  liste : c'est la ligne qu'on cherche, pas
                                  un detail qu'on lit quand on y pense. */}
                              {g.remboursement > 0 && (
                                <div className="text-xs font-bold text-red-600 leading-tight mt-1">
                                  remboursement : − {formatPrice(g.remboursement)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            STATUS_COLORS[order.status as OrderStatus] ||
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {STATUS_LABELS[order.status as OrderStatus] ||
                            order.status}
                        </span>
                        {/* Commande du site : ou en est la livraison. */}
                        <div>
                          <BadgeLivraison l={(order as any).livraison} />
                        </div>
                        <div>
                          <BadgePaiement order={order} />
                        </div>
                        {/* Le temps de preparation, SOUS le statut.
                            Sa place est ici et pas ailleurs : le badge dit
                            « En préparation » pour dix commandes a la fois,
                            et ce chiffre est la seule chose qui distingue
                            celle qui vient d'arriver de celle qui traine.
                            Les deux se lisent ensemble ou ne se lisent pas.

                            On passe par tempsPreparation() plutot que par
                            infosGlovo() : la premiere sait aussi compter le
                            temps d'une commande PAS ENCORE prete, la seconde
                            ne connait que les commandes finies. */}
                        {(() => {
                          const t = tempsPreparation(order);
                          // Pas encore de fichier pour cette commande : on
                          // le dit en gris. Un tiret muet laisserait croire
                          // a une preparation instantanee.
                          if (t.enAttente || t.minutes === null) {
                            // Le fichier Glovo ne concerne que les commandes
                            // Glovo : rien a attendre pour le site (10/10/2026).
                            if (nomSource(order.source) !== "GLOVO") return null;
                            return (
                              <div className="text-[11px] text-gray-400 leading-tight mt-1">
                                préparation : fichier Glovo attendu
                              </div>
                            );
                          }
                          // Sous 15 minutes tout va bien, au-dela il faut
                          // regarder. Deux couleurs, pas trois : on ne doit
                          // pas avoir a se rappeler ce que veut dire l'orange.
                          const dansLesTemps = t.minutes < 15;
                          return (
                            <div
                              className={`text-xs font-semibold tabular-nums leading-tight mt-1 flex items-center gap-1 ${
                                dansLesTemps
                                  ? "text-green-700"
                                  : "text-red-700"
                              }`}
                            >
                              <Clock className="w-3 h-3 shrink-0" />
                              {`préparée en ${t.minutes} min`}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(
                          (order as any).orderTime || order.createdAt,
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          {canEditPhone && estAnnulable(order) && (
                            <button
                              onClick={() => setAnnulerDepuisListe(order)}
                              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center gap-1"
                              title="Annuler la commande"
                            >
                              <XCircle className="w-3 h-3" />
                              Annuler
                            </button>
                          )}
                          {!canEditPhone &&
                            canManageOrders &&
                            order.status === OrderStatus.PENDING && (
                              <>
                                <button
                                  onClick={() => handleCancel(order.id)}
                                  disabled={processing === order.id}
                                  className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                                  title="Annuler"
                                >
                                  <XCircle className="w-3 h-3" />
                                  Annuler
                                </button>
                              </>
                            )}
                          <button
                            onClick={async () => {
                              // Charger les détails complets (avec baskets)
                              try {
                                console.log(
                                  "Loading order details for:",
                                  order.id,
                                );
                                const fullOrder = await api.orders.getById(
                                  order.id,
                                );
                                console.log("Order details loaded:", fullOrder);
                                setSelectedOrder(fullOrder);
                              } catch (error) {
                                console.error(
                                  "Error loading order details:",
                                  error,
                                );
                                // Fallback sur l'order de base si erreur
                                setSelectedOrder(order);
                              }
                            }}
                            className="text-primary hover:text-primary-dark flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            Voir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Page {currentPage} sur {totalPages} ({totalOrders} commandes au
                total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages || loading}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal détails commande */}
      {annulerDepuisListe && canEditPhone && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAnnulerDepuisListe(null);
          }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                Commande{" "}
                {(annulerDepuisListe as any).orderCode ||
                  (annulerDepuisListe as any).externalId ||
                  annulerDepuisListe.id}
              </h2>
              <button
                onClick={() => setAnnulerDepuisListe(null)}
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <AnnulerCommande
              order={annulerDepuisListe}
              ouvertAuDepart
              onFermer={() => setAnnulerDepuisListe(null)}
              onDone={(m) => {
                showToast(m, "success");
                queryClient.invalidateQueries({ queryKey: ["orders"] });
                queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                setAnnulerDepuisListe(null);
              }}
              onError={(m) => showToast(m, "error")}
            />
          </div>
        </div>
      )}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedOrder(null);
          }}
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 md:px-6 py-4 flex justify-between items-center border-b border-primary/20">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                Détails de la commande
              </h2>
              <div className="flex items-center gap-2">
                {/* LA FACTURE DU CLIENT (10/10/2026) : prix de vente
                    seulement, generee dans le navigateur. Direction et
                    support seulement. */}
                {canEditPhone && (
                <button
                  onClick={() => {
                    try {
                      telechargerFacture(donneesFacture(selectedOrder));
                    } catch (e: any) {
                      showToast(e?.message || "Facture impossible à générer", "error");
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Facture PDF
                </button>
                )}
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-white/50 hover:text-gray-700 transition-colors"
                >
                  <XCircle className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>
            </div>
            <div className="p-4 md:p-6 overflow-y-auto space-y-6">
              {canEditPhone && (
                <AnnulerCommande
                  order={selectedOrder}
                  onDone={async (m) => {
                    showToast(m, "success");
                    queryClient.invalidateQueries({ queryKey: ["orders"] });
                    queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                    const id = selectedOrder?.id;
                    if (!id) return;
                    try {
                      setSelectedOrder(await api.orders.getById(id));
                    } catch {
                      setSelectedOrder(null);
                    }
                  }}
                  onError={(m) => showToast(m, "error")}
                />
              )}
              {/* GLOVO A-T-IL ETE PREVENU ?
                  L'application enregistrait deja l'echec, mais nulle part a
                  l'ecran : on croyait la commande partie alors qu'elle
                  restait bloquee chez Glovo. Un echec silencieux est pire
                  qu'une panne visible. */}
              {(() => {
                const m: any = (selectedOrder as any)?.metadata || {};
                const soucis: string[] = [];
                if (m.acceptance && m.acceptance.glovoApiCalled === false) {
                  soucis.push(
                    `Acceptation : ${m.acceptance.glovoError || "Glovo n'a pas été prévenu"}`,
                  );
                }
                if (m.ready && m.ready.glovoApiCalled === false) {
                  soucis.push(
                    `Marquée prête : ${m.ready.glovoError || "Glovo n'a pas été prévenu"}`,
                  );
                }
                if (soucis.length === 0) return null;
                return (
                  <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4">
                    <p className="text-sm font-bold text-red-800">
                      Glovo n&apos;a pas suivi
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {soucis.map((x, i) => (
                        <li key={i} className="text-xs text-red-700">
                          {x}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-red-600">
                      Chez nous la commande a avancé ; chez Glovo, non. Elle
                      doit être traitée sur le semi.
                    </p>
                  </div>
                );
              })()}

              {/* Informations générales */}
              <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Informations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Code commande
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {(selectedOrder as any).orderCode ||
                        selectedOrder.externalId ||
                        selectedOrder.id}
                    </p>
                    {(selectedOrder as any).orderCode &&
                      selectedOrder.externalId && (
                        <p className="text-xs text-gray-500">
                          ID Glovo: {selectedOrder.externalId}
                        </p>
                      )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Statut
                    </label>
                    <p className="text-sm">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          STATUS_COLORS[selectedOrder.status] ||
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {STATUS_LABELS[selectedOrder.status] ||
                          selectedOrder.status}
                      </span>
                    </p>

                    {/* Avertissement durable : l'application a change le statut
                        mais Glovo n'a pas ete prevenu. */}
                    {(() => {
                      const meta: any = (selectedOrder as any).metadata || {};
                      const echec =
                        (meta.ready && meta.ready.glovoApiCalled === false && meta.ready.glovoError) ||
                        (meta.acceptance && meta.acceptance.glovoApiCalled === false && meta.acceptance.glovoError);
                      if (!echec) return null;
                      return (
                        <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-2.5 py-2">
                          <p className="text-xs font-semibold text-red-800">
                            Glovo n'a pas été prévenu
                          </p>
                          <p className="mt-0.5 text-[11px] leading-snug text-red-700 break-words">
                            {String(echec)}
                          </p>
                          <p className="mt-1 text-[11px] text-red-600">
                            Le statut a changé dans l'application uniquement. Fais l'opération sur le semi.
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Client
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrder.customerName}
                    </p>

                    <CustomerPhoneCell
                      order={selectedOrder}
                      canEdit={canEditPhone}
                      onSaved={async () => {
                        handlePhoneSaved();
                        const maj = await api.orders.getById(selectedOrder.id);
                        setSelectedOrder(maj);
                      }}
                      onError={(m) => showToast(m, "error")}
                    />
                  </div>
                  {voitPrix && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Montant total
                    </label>
                    <p className="text-sm font-medium text-gray-900">
                      {formatPrice(selectedOrder.totalAmount)}
                    </p>
                  </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Source
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrder.source}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Store
                    </label>
                    <p className="text-sm text-gray-900">
                      {(selectedOrder as any).store?.name || "—"}
                    </p>
                    {(selectedOrder as any).store?.glovoStoreId &&
                      nomSource(selectedOrder.source) === "GLOVO" && (
                      <p className="text-xs text-gray-500">
                        Glovo: {(selectedOrder as any).store.glovoStoreId}
                      </p>
                    )}
                  </div>
                  {selectedOrder.source === "WEBSITE" && (
                    <div className="col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <label className="text-sm font-medium text-emerald-800">
                        Livraison — site web
                      </label>
                      <p className="text-sm text-gray-900 mt-1">
                        📍 {(selectedOrder as any).deliveryAddress || "Adresse non fournie"}
                      </p>
                      {(selectedOrder as any).routage ? (
                        <div className="text-xs text-gray-700 mt-2 space-y-1">
                          <p>
                            Magasin le plus proche :{" "}
                            <b>{(selectedOrder as any).routage.magasin}</b> —{" "}
                            {(selectedOrder as any).routage.distanceKm} km
                            {(selectedOrder as any).routage.dureeMin != null &&
                              `, ~${(selectedOrder as any).routage.dureeMin} min`}
                            {(selectedOrder as any).routage.methode === "vol_oiseau" &&
                              " (à vol d'oiseau)"}
                          </p>
                          <p className="text-gray-500">
                            {((selectedOrder as any).routage.candidats || [])
                              .map((c: any) => `${c.magasin} ${c.distanceKm} km${c.dureeMin != null ? ` / ${c.dureeMin} min` : ""}`)
                              .join("  ·  ")}
                          </p>
                          {(selectedOrder as any).routage.tropLoin && (
                            <p className="text-red-600 font-medium">
                              ⚠ Client au-delà de {(selectedOrder as any).routage.seuilKm} km — support prévenu
                            </p>
                          )}
                          <a
                            href={(selectedOrder as any).routage.lienMaps}
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-700 underline"
                          >
                            Ouvrir l'itinéraire dans Google Maps
                          </a>
                        </div>
                      ) : (
                        <p className="text-xs text-red-600 mt-2 font-medium">
                          ⚠ Adresse non localisée — choisir le magasin à la main
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-600">Magasin qui traite :</span>
                        <select
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                          value={selectedOrder.storeId || ""}
                          onChange={async (e) => {
                            try {
                              await api.orders.attribuerMagasin(selectedOrder.id, e.target.value);
                              showToast("Magasin mis à jour", "success");
                              setSelectedOrder({ ...(selectedOrder as any), storeId: e.target.value });
                            } catch (err: any) {
                              showToast(err?.message || "Erreur", "error");
                            }
                          }}
                        >
                          {stores.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {voitPrix && (
                      <LivraisonPaiementSite
                        order={selectedOrder}
                        peutModifier={canEditPhone}
                        onSaved={(maj) => {
                          showToast("Livraison et paiement enregistrés", "success");
                          setSelectedOrder({
                            ...(selectedOrder as any),
                            ...maj,
                            fraisLivraisonAFixer: false,
                          });
                          void queryClient.invalidateQueries({ queryKey: ["orders"] });
                        }}
                        onError={(m) => showToast(m, "error")}
                      />
                      )}
                      {canEditPhone ? (
                        <EncaissementSite
                          order={selectedOrder}
                          onSaved={(maj) => {
                            showToast(
                              maj.paiementEncaisse ? "Paiement encaissé" : "Paiement remis en attente",
                              "success",
                            );
                            setSelectedOrder({ ...(selectedOrder as any), ...maj });
                            void queryClient.invalidateQueries({ queryKey: ["orders"] });
                          }}
                          onError={(m) => showToast(m, "error")}
                        />
                      ) : (
                        <div className="mt-2">
                          <BadgePaiement order={selectedOrder} />
                        </div>
                      )}
                      {canEditPhone && (
                        <AjouterArticleSite
                          order={selectedOrder}
                          onDone={async (message) => {
                            showToast(message, "success");
                            void queryClient.invalidateQueries({ queryKey: ["orders"] });
                            try {
                              setSelectedOrder(await api.orders.getById(selectedOrder.id));
                            } catch {
                              /* la liste se recharge de toute facon */
                            }
                          }}
                          onError={(m) => showToast(m, "error")}
                        />
                      )}
                      {canEditPhone && (
                        <RemettreEnPreparation
                          order={selectedOrder}
                          onDone={async (message) => {
                            showToast(message, "success");
                            void queryClient.invalidateQueries({ queryKey: ["orders"] });
                            void queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                            try {
                              setSelectedOrder(await api.orders.getById(selectedOrder.id));
                            } catch {
                              /* la liste se recharge de toute facon */
                            }
                          }}
                          onError={(m) => showToast(m, "error")}
                        />
                      )}
                      {canEditPhone && (
                        <RegulariserLivree
                          order={selectedOrder}
                          onDone={async (message) => {
                            showToast(message, "success");
                            void queryClient.invalidateQueries({ queryKey: ["orders"] });
                            void queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                            try {
                              setSelectedOrder(await api.orders.getById(selectedOrder.id));
                            } catch {
                              /* la liste se recharge de toute facon */
                            }
                          }}
                          onError={(m) => showToast(m, "error")}
                        />
                      )}
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Date de création
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(
                        (selectedOrder as any).orderTime ||
                          selectedOrder.createdAt,
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Code retrait
                    </label>
                    <p className="text-sm text-gray-900 font-mono font-semibold">
                      {(selectedOrder as any).metadata?.pick_up_code ||
                        (selectedOrder as any).pickUpCode ||
                        "—"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">
                      Allergies / Intolérances
                    </label>
                    <p
                      className={`text-sm rounded-lg p-2 mt-0.5 ${(selectedOrder as any).allergyInfo ? "text-amber-800 bg-amber-50" : "text-gray-500 bg-gray-50"}`}
                    >
                      {(selectedOrder as any).allergyInfo || "—"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">
                      Instructions spéciales
                    </label>
                    <p className="text-sm text-gray-900 rounded-lg p-2 bg-white border border-gray-100">
                      {(selectedOrder as any).specialRequirements || "—"}
                    </p>
                  </div>
                </div>

                {shouldShowOrderCancellation(selectedOrder) && (
                  <div className="mt-4 pt-4 border-t border-red-100 space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-red-800/90">
                      Annulation
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-500">
                          Raison (Glovo)
                        </label>
                        <p className="text-sm text-gray-900 font-medium mt-0.5">
                          {formatGlovoCancelReason(selectedOrder.cancellationReason)}
                        </p>
                        {selectedOrder.cancellationReason?.trim() && (
                          <p className="text-xs text-gray-500 font-mono mt-1">
                            {selectedOrder.cancellationReason}
                          </p>
                        )}
                      </div>
                      {(selectedOrder as any).cancelledAt != null &&
                        String((selectedOrder as any).cancelledAt) !== "" && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Annulée le
                            </label>
                            <p className="text-sm text-gray-900">
                              {formatDate((selectedOrder as any).cancelledAt)}
                            </p>
                          </div>
                        )}
                      {(() => {
                        const n = selectedOrder.notes?.trim();
                        const r = selectedOrder.cancellationReason?.trim();
                        const redundant =
                          !!n && !!r && n === `Annulée par Glovo: ${r}`;
                        if (!n || redundant) return null;
                        return (
                          <div className="md:col-span-2">
                            <label className="text-sm font-medium text-gray-500">
                              Notes
                            </label>
                            <p className="text-sm text-gray-900 rounded-lg p-2 bg-white border border-red-50">
                              {n}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Items */}
              {(selectedOrder as any).items &&
                (selectedOrder as any).items.length > 0 && (
                  <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Produits
                    </h3>
                    {/* Vue mobile : Liste */}
                    <div className="md:hidden space-y-2">
                      {(selectedOrder as any).items.map(
                        (item: any, idx: number) => (
                          <div
                            key={idx}
                            className="border border-gray-100 rounded-lg p-3 bg-white flex gap-3 shadow-sm"
                          >
                            {item.productImageUrl && (
                              <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-md bg-white border border-gray-200">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={photoReduite(item.productImageUrl, 160)}
                                  alt={item.productName || "Produit"}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-1">
                                <div className="text-sm font-medium text-gray-900 flex-1">
                                  {item.productName}
                                </div>
                                {voitPrix && (
                                <div className="text-sm font-semibold text-gray-900 ml-2">
                                  {formatPrice(prixUnitaire(item))}
                                </div>
                                )}
                              </div>
                              {!isFournisseur && (
                                <FournisseurDeLArticle item={item} order={selectedOrder} />
                              )}
                              <div className="text-xs text-gray-500">
                                Quantité:{" "}
                                {/* PLUS D'UN, EN ROUGE — meme regle que chez
                                    le fournisseur : c'est ce qu'on oublie le
                                    plus, et ca revient en remboursement. */}
                                <span
                                  className={
                                    Number(item.quantity) > 1
                                      ? "font-black text-red-600 text-sm"
                                      : ""
                                  }
                                >
                                  {item.quantity}
                                </span>
                                {Number(item.poidsUnitaire) > 0 && (
                                  <span className="ml-2 font-semibold text-gray-700">
                                    {Number(item.quantity) > 1
                                      ? `${Number(item.quantity)} × ${Number(item.poidsUnitaire)} kg = ${(Number(item.quantity) * Number(item.poidsUnitaire)).toFixed(2).replace(/\.00$/, "")} kg`
                                      : `${Number(item.poidsUnitaire)} kg`}
                                  </span>
                                )}
                              </div>
                              {/* CE QU'ON PAIE ET DANS QUOI ON EMBALLE.
                                  Le fournisseur et le responsable commande
                                  l'avaient deja ; l'admin ne voyait que le
                                  prix de vente. */}
                              <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                                {voitPrix && Number(item.prixAchatUnitaire) > 0 && (
                                  <span>
                                    Achat :{" "}
                                    {Number(item.prixAchatUnitaire)
                                      .toFixed(2)
                                      .replace(/\.00$/, "")}{" "}
                                    DH
                                  </span>
                                )}
                                {photoEmballage(item.nomEmballage) ? (
                                  <img
                                    src={photoEmballage(item.nomEmballage) as string}
                                    alt={String(item.nomEmballage || "")}
                                    title={String(item.nomEmballage || "")}
                                    className="h-8 w-12 object-contain rounded border border-gray-200 bg-white"
                                  />
                                ) : (
                                  item.nomEmballage &&
                                  String(item.nomEmballage).toLowerCase() !==
                                    "origine" && (
                                    <span>{String(item.nomEmballage)}</span>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                      {voitPrix && <TotauxArticles items={(selectedOrder as any).items} />}
                    </div>
                    {/* Vue desktop : Tableau */}
                    <div className="hidden md:block border border-gray-100 rounded-lg overflow-hidden bg-white shadow-sm">
                      <table className="w-full">
                        <thead className="bg-gray-50/80">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Photo
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Produit
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Quantité
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Emballage
                            </th>
                            {voitPrix && (
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Prix d&apos;achat
                            </th>
                            )}
                            {voitPrix && (
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Prix
                            </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {(selectedOrder as any).items.map(
                            (item: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-2 text-sm">
                                  {item.productImageUrl ? (
                                    <div className="w-14 h-14 overflow-hidden rounded-md bg-white border border-gray-200">
                                      {/* eslint-disable-next-line @next/next/no-img-element */}
                                      <img
                                        src={photoReduite(item.productImageUrl, 160)}
                                        alt={item.productName || "Produit"}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">
                                      Aucune
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <span className={item.retireParGlovo || item.retrait?.direct ? "line-through text-gray-400" : ""}>
                                    {item.productName}
                                  </span>
                                  {item.retireParGlovo && !item.retrait?.direct && (
                                    <span className="block text-[11px] font-semibold text-red-600">
                                      retiré par Glovo — non compté
                                    </span>
                                  )}
                                  {item.retrait?.direct && (
                                    <span className="block text-[11px] font-semibold text-red-600">
                                      retiré par {item.retrait.demandeParNom || "l'équipe"} — ventes perdues
                                      {item.retrait.statut === "A_RETIRER_TABLETTE" && " (à retirer sur la tablette Glovo)"}
                                    </span>
                                  )}
                                  {!isFournisseur && (
                                    <FournisseurDeLArticle item={item} order={selectedOrder} />
                                  )}
                                  {!isFournisseur && canManageOrders && (
                                    <RetirerArticle
                                      item={item}
                                      order={selectedOrder}
                                      estSupport={canEditPhone}
                                      onDone={async (m) => {
                                        showToast(m, "success");
                                        void queryClient.invalidateQueries({ queryKey: ["orders"] });
                                        try {
                                          setSelectedOrder(await api.orders.getById(selectedOrder.id));
                                        } catch {
                                          /* la liste se recharge de toute facon */
                                        }
                                      }}
                                      onError={(m) => showToast(m, "error")}
                                    />
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <span
                                    className={
                                      Number(item.quantity) > 1
                                        ? "font-black text-red-600 text-base"
                                        : ""
                                    }
                                  >
                                    {item.quantity}
                                  </span>
                                  {Number(item.poidsUnitaire) > 0 && (
                                    <div className="text-[11px] font-semibold text-gray-600">
                                      {Number(item.quantity) > 1
                                        ? `${Number(item.quantity)} × ${Number(item.poidsUnitaire)} kg = ${(Number(item.quantity) * Number(item.poidsUnitaire)).toFixed(2).replace(/\.00$/, "")} kg`
                                        : `${Number(item.poidsUnitaire)} kg`}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {photoEmballage(item.nomEmballage) ? (
                                    <img
                                      src={photoEmballage(item.nomEmballage) as string}
                                      alt={String(item.nomEmballage || "")}
                                      title={String(item.nomEmballage || "")}
                                      className="h-10 w-14 object-contain rounded border border-gray-200 bg-white"
                                    />
                                  ) : item.nomEmballage &&
                                    String(item.nomEmballage).toLowerCase() !==
                                      "origine" ? (
                                    <span className="text-xs text-gray-600">
                                      {String(item.nomEmballage)}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                {voitPrix && (
                                <td className="px-4 py-2 text-sm">
                                  {Number(item.prixAchatUnitaire) > 0 ? (
                                    <span>
                                      {Number(item.prixAchatUnitaire)
                                        .toFixed(2)
                                        .replace(/\.00$/, "")}{" "}
                                      DH
                                      {Number(item.quantity) > 1 && (
                                        <span className="block text-[11px] font-semibold text-gray-500">
                                          × {Number(item.quantity)} ={" "}
                                          {enDH(
                                            Math.round(Number(item.prixAchatUnitaire) * 100) *
                                              Number(item.quantity),
                                          )}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                )}
                                {voitPrix && (
                                <td className="px-4 py-2 text-sm">
                                  {formatPrice(prixUnitaire(item))}
                                  {Number(item.quantity) > 1 && (
                                    <span className="block text-[11px] font-semibold text-gray-500">
                                      × {Number(item.quantity)} ={" "}
                                      {enDH(prixUnitaire(item) * Number(item.quantity))}
                                    </span>
                                  )}
                                  {item.prixVenteAvantCorrection != null &&
                                    Number(item.prixVenteAvantCorrection) !== prixUnitaire(item) && (
                                      <span className="block text-[11px] text-gray-400 line-through">
                                        {enDH(Number(item.prixVenteAvantCorrection))}
                                      </span>
                                    )}
                                  {canEditPhone && (
                                    <ModifierPrixVente
                                      item={item}
                                      order={selectedOrder}
                                      onDone={async (m) => {
                                        showToast(m, "success");
                                        void queryClient.invalidateQueries({ queryKey: ["orders"] });
                                        void queryClient.invalidateQueries({ queryKey: ["orderStats"] });
                                        try {
                                          setSelectedOrder(await api.orders.getById(selectedOrder.id));
                                        } catch {
                                          /* la fenetre se rafraichira a la prochaine ouverture */
                                        }
                                      }}
                                      onError={(m) => showToast(m, "error")}
                                    />
                                  )}
                                </td>
                                )}
                              </tr>
                            ),
                          )}
                        </tbody>
                        {/* LES TOTAUX, quantites comprises. Pas pour le
                            responsable commande (02/11/2026). */}
                        {voitPrix && (() => {
                          const tt = totauxArticles((selectedOrder as any).items);
                          return (
                            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                              <tr>
                                <td colSpan={4} className="px-4 py-2 text-sm font-bold text-gray-900 text-right">
                                  Total
                                  {tt.sansAchat > 0 && (
                                    <span className="block text-[11px] font-medium text-amber-700">
                                      {tt.sansAchat} article{tt.sansAchat > 1 ? "s" : ""} sans prix d&apos;achat
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm font-bold text-gray-900 tabular-nums">
                                  {enDH(tt.achatCentimes)}
                                </td>
                                <td className="px-4 py-2 text-sm font-bold text-gray-900 tabular-nums">
                                  {enDH(tt.venteCentimes)}
                                </td>
                              </tr>
                              <tr>
                                <td colSpan={4} className="px-4 pb-2 text-xs text-gray-500 text-right">
                                  Différence vente − achat (avant commission et emballage)
                                </td>
                                <td
                                  colSpan={2}
                                  className={`px-4 pb-2 text-sm font-bold tabular-nums ${
                                    tt.ecartCentimes >= 0 ? "text-emerald-700" : "text-red-700"
                                  }`}
                                >
                                  {enDH(tt.ecartCentimes)}
                                </td>
                              </tr>
                              {/* LA LIVRAISON DU SITE (02/11/2026) : payee par le
                                  client, ou offerte et alors a notre charge. */}
                              {(selectedOrder as any).source === "WEBSITE" &&
                                Number((selectedOrder as any).deliveryFee) > 0 &&
                                (() => {
                                  const so: any = selectedOrder;
                                  const fraisL = Math.round(Number(so.deliveryFee) || 0);
                                  const offerteL = so.livraisonOfferte === true;
                                  return (
                                    <>
                                      <tr>
                                        <td colSpan={4} className="px-4 pb-1 text-xs text-gray-500 text-right">
                                          {offerteL
                                            ? "Livraison offerte (à notre charge, répartie sur les articles)"
                                            : "Livraison payée par le client"}
                                        </td>
                                        <td
                                          colSpan={2}
                                          className={`px-4 pb-1 text-sm font-semibold tabular-nums ${
                                            offerteL ? "text-red-700" : "text-gray-700"
                                          }`}
                                        >
                                          {offerteL ? `− ${enDH(fraisL)}` : `+ ${enDH(fraisL)}`}
                                        </td>
                                      </tr>
                                      {offerteL && (
                                        <tr>
                                          <td colSpan={4} className="px-4 pb-1 text-xs text-gray-500 text-right">
                                            Différence après livraison
                                          </td>
                                          <td
                                            colSpan={2}
                                            className={`px-4 pb-1 text-sm font-bold tabular-nums ${
                                              tt.ecartCentimes - fraisL >= 0 ? "text-emerald-700" : "text-red-700"
                                            }`}
                                          >
                                            {enDH(tt.ecartCentimes - fraisL)}
                                          </td>
                                        </tr>
                                      )}
                                      <tr>
                                        <td colSpan={4} className="px-4 pb-2 text-sm font-bold text-gray-900 text-right">
                                          Le client paie
                                        </td>
                                        <td colSpan={2} className="px-4 pb-2 text-sm font-bold text-gray-900 tabular-nums">
                                          {enDH(Math.round(Number(so.totalAmount) || 0))}
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })()}
                            </tfoot>
                          );
                        })()}
                      </table>
                    </div>
                  </div>
                )}

              {canEditPhone && !isFournisseur && (
                <LivreurSupplementaire
                  order={selectedOrder}
                  estAdmin={user?.role === UserRole.ADMIN}
                  onDone={async (m) => {
                    showToast(m, "success");
                    void queryClient.invalidateQueries({ queryKey: ["orders"] });
                    try {
                      setSelectedOrder(await api.orders.getById(selectedOrder.id));
                    } catch {
                      /* rafraichi a la prochaine ouverture */
                    }
                  }}
                  onError={(m) => showToast(m, "error")}
                />
              )}

              {/* AJOUTER UN ARTICLE — commandes Glovo et Kooul (11/10/2026).
                  Celles du site ont le meme bouton dans leur bloc vert. */}
              {canEditPhone && !isFournisseur && selectedOrder.source !== "WEBSITE" && (
                <div>
                  <AjouterArticleSite
                    order={selectedOrder}
                    onDone={async (message) => {
                      showToast(message, "success");
                      void queryClient.invalidateQueries({ queryKey: ["orders"] });
                      try {
                        setSelectedOrder(await api.orders.getById(selectedOrder.id));
                      } catch {
                        /* la liste se recharge de toute facon */
                      }
                    }}
                    onError={(m) => showToast(m, "error")}
                  />
                </div>
              )}

              {/* Paniers (si disponibles) */}
              {(selectedOrder as any).baskets &&
                (selectedOrder as any).baskets.length > 0 && (
                  <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Paniers {isFournisseur && "(mes paniers)"}
                    </h3>
                    <div className="space-y-2">
                      {(selectedOrder as any).baskets
                        .filter(
                          (basket: any) =>
                            !isFournisseur ||
                            basket.fournisseurId === user?.fournisseurId,
                        )
                        .map((basket: any) => (
                          <div
                            key={basket.id}
                            className="border border-gray-100 rounded-lg p-3 bg-white flex justify-between items-center shadow-sm"
                          >
                            <div className="flex-1">
                              <span className="font-medium">
                                {basket.basketNumber}
                              </span>
                              {!isFournisseur && basket.fournisseurName && (
                                <span className="ml-2 text-xs text-gray-600">
                                  {basket.fournisseurName}
                                </span>
                              )}
                              <span
                                className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                  basket.status === "PICKED_UP"
                                    ? "bg-green-100 text-green-800"
                                    : basket.status === "CANCELLED"
                                      ? "bg-gray-100 text-gray-500 line-through"
                                    : basket.status === "READY"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {basket.status === "PICKED_UP"
                                  ? "Récupéré"
                                  : basket.status === "CANCELLED"
                                    ? "Annulé"
                                  : basket.status === "READY"
                                    ? "Prêt"
                                    : basket.status === "PREPARING"
                                      ? "En préparation"
                                      : "En attente"}
                              </span>
                            </div>
                            {/* Fournisseur: Marquer prêt */}
                            {isFournisseur &&
                              basket.fournisseurId === user?.fournisseurId &&
                              (basket.status === BasketStatus.PENDING ||
                                basket.status === BasketStatus.PREPARING) && (
                                <button
                                  onClick={() =>
                                    handleBasketMarkReady(basket.id)
                                  }
                                  disabled={processing === basket.id}
                                  className="ml-2 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  {processing === basket.id
                                    ? "..."
                                    : "Marquer prêt"}
                                </button>
                              )}
                            {/* Collaborateur: Récupérer le panier */}
                            {isCollaborateur && basket.status === "READY" && (
                              <button
                                onClick={() => handleBasketPickup(basket.id)}
                                disabled={processing === basket.id}
                                className="ml-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                              >
                                <CheckCircle className="w-4 h-4" />
                                {processing === basket.id ? "..." : "Récupérer"}
                              </button>
                            )}
                          </div>
                        ))}
                    </div>
                    {canEditPhone && !isFournisseur && (
                      <DeplacerArticle
                        order={selectedOrder}
                        onDone={async (message) => {
                          showToast(message, "success");
                          void queryClient.invalidateQueries({ queryKey: ["orders"] });
                          try {
                            const maj = await api.orders.getById(selectedOrder.id);
                            setSelectedOrder(maj);
                          } catch {
                            /* la liste se recharge de toute facon */
                          }
                        }}
                        onError={(m) => showToast(m, "error")}
                      />
                    )}
                  </div>
                )}

              {/* SANS PANIER, ON DOIT POUVOIR AFFECTER QUAND MEME (25/10/2026).
                  Une commande dont tous les articles sont en caisse (volaille
                  achetee par le responsable, par exemple) n'a aucun panier :
                  la section « Paniers » ne s'affichait pas, et avec elle le
                  formulaire pour affecter un article a la caisse de Hakim ou
                  de Jean avec son prix d'achat. */}
              {canEditPhone && !isFournisseur &&
                !((selectedOrder as any).baskets && (selectedOrder as any).baskets.length > 0) && (
                <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                  {(
                    <DeplacerArticle
                      order={selectedOrder}
                      onDone={async (message) => {
                        showToast(message, "success");
                        void queryClient.invalidateQueries({ queryKey: ["orders"] });
                        try {
                          const maj = await api.orders.getById(selectedOrder.id);
                          setSelectedOrder(maj);
                        } catch {
                          /* la liste se recharge de toute facon */
                        }
                      }}
                      onError={(m) => showToast(m, "error")}
                    />
                  )}
                </div>
              )}

              {/* Actions pour collaborateur/admin/support */}
              {canManageOrders && (
                <div className="rounded-xl bg-gray-50/80 p-4 border border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {selectedOrder.status === OrderStatus.PENDING && (
                      <>
                        {/* LE RATTRAPAGE, PAS LE GESTE DU QUOTIDIEN.
                            Une commande est acceptée toute seule dès que
                            Glovo l'envoie. Si elle est encore ici, c'est que
                            l'acceptation automatique a échoué — ce bouton
                            rejoue exactement la même opération. */}
                        {/* UNE COMMANDE DU SITE N'EST PAS UN RATE.
                            Elle attend un livreur : c'est le support qui la
                            valide quand il en a trouve un. Meme operation que
                            le rattrapage, mais ce n'est pas le meme geste —
                            et le bouton doit le dire. */}
                        {selectedOrder.source === "WEBSITE" ? (
                          (() => {
                            // L'application livreurs dit si un livreur a
                            // accepte. Tant que non, le bouton reste la —
                            // un livreur trouve par telephone compte aussi —
                            // mais il le dit, pour qu'on ne valide pas une
                            // commande que personne ne viendra chercher.
                            const l = (selectedOrder as any).livraison as
                              | LivraisonCommande
                              | undefined;
                            const trouve = l?.statut === "acceptee";
                            const enRecherche =
                              !!l && ["brouillon", "en_attente", "proposee"].includes(String(l.statut));
                            return (
                              <div className="w-full flex flex-col gap-2">
                                {l && <BadgeLivraison l={l} />}
                                <button
                                  onClick={() => handleAccept(selectedOrder.id)}
                                  disabled={processing === selectedOrder.id}
                                  title="Un livreur est trouvé : la commande part chez les fournisseurs et le client est prévenu"
                                  className={`self-start px-4 py-2 text-white rounded disabled:opacity-50 flex items-center gap-2 ${
                                    enRecherche
                                      ? "bg-gray-500 hover:bg-gray-600"
                                      : "bg-emerald-600 hover:bg-emerald-700"
                                  }`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  {processing === selectedOrder.id
                                    ? "..."
                                    : trouve
                                      ? `Livreur trouvé${l?.livreurPrenom ? ` : ${l.livreurPrenom}` : ""} — valider la commande`
                                      : enRecherche
                                        ? "Valider quand même (livreur trouvé hors application)"
                                        : "Livreur trouvé — valider la commande"}
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <button
                            onClick={() => handleAccept(selectedOrder.id)}
                            disabled={processing === selectedOrder.id}
                            title="L'acceptation automatique a échoué : rejouer l'opération"
                            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {processing === selectedOrder.id
                              ? "..."
                              : "Forcer l'acceptation"}
                          </button>
                        )}
                        <button
                          onClick={() => handleCancel(selectedOrder.id)}
                          disabled={processing === selectedOrder.id}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Annuler
                        </button>
                      </>
                    )}
                    {(selectedOrder.status === OrderStatus.IN_PREPARATION ||
                      selectedOrder.status === OrderStatus.GLOVO_ACCEPTED) &&
                      (selectedOrder as any).baskets &&
                      (selectedOrder as any).baskets.length > 0 &&
                      (selectedOrder as any).baskets.every(
                        (b: any) => b.status === "PICKED_UP",
                      ) && (
                        <button
                          onClick={() => handleMarkReady(selectedOrder.id)}
                          disabled={processing === selectedOrder.id}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {processing === selectedOrder.id
                            ? "..."
                            : "Marquer comme prête"}
                        </button>
                      )}
                    {(selectedOrder.status === OrderStatus.IN_PREPARATION ||
                      selectedOrder.status === OrderStatus.GLOVO_ACCEPTED) &&
                      (!(selectedOrder as any).baskets ||
                        (selectedOrder as any).baskets.length === 0) && (
                        <button
                          onClick={() => handleMarkReady(selectedOrder.id)}
                          disabled={processing === selectedOrder.id}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {processing === selectedOrder.id
                            ? "..."
                            : "Marquer comme prête"}
                        </button>
                      )}
                  </div>
                </div>
              )}

              <EssaiAjoutArticle
                order={selectedOrder}
                estAdmin={user?.role === UserRole.ADMIN}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}


/* ==========================================================================
 *  ESSAI — ajouter un article a une commande Glovo deja passee
 * ==========================================================================
 *
 *  POURQUOI CET ENCART EXISTE
 *
 *  Une cliente rappelle apres avoir commande et veut un article de plus.
 *  Le portail Glovo ne permet pas de modifier la commande ; son API v2 si,
 *  avec `UPDATE_CART` et un article en `ADDITION`.
 *
 *  Sauf que nos actions sur les commandes passent par l'API v0, pas la v2.
 *  Deux choses restent donc a verifier sur une VRAIE commande : que
 *  `UPDATE_CART` soit ouvert sur le contrat marocain, et dans quelle unite
 *  Glovo attend les montants.
 *
 *  Cet encart sert a repondre a ces deux questions, pas a faire travailler
 *  le support. Il est reserve a l'administration, il n'enregistre RIEN, et
 *  il montre toujours l'apercu avant de laisser envoyer quoi que ce soit.
 *  L'ecran du support viendra apres, quand on saura.
 * ======================================================================== */
function EssaiAjoutArticle({
  order,
  estAdmin,
}: {
  order: any;
  estAdmin: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState("");
  const [resultats, setResultats] = useState<any[]>([]);
  const [cherche, setCherche] = useState(false);
  const [choisi, setChoisi] = useState<any | null>(null);
  const [prix, setPrix] = useState("");
  const [quantite, setQuantite] = useState("1");
  const [travail, setTravail] = useState(false);
  const [apercu, setApercu] = useState<any | null>(null);
  const [reponse, setReponse] = useState<any | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  // Hors de ces deux cas, l'encart n'a rien a faire la : le support ne doit
  // pas le voir, et une commande qui ne vient pas de Glovo n'a pas d'API ou
  // pousser un article.
  if (!estAdmin) return null;
  if (order?.source !== "GLOVO") return null;

  const totalMad = Number(order?.totalAmount || 0) / 100;

  async function chercher() {
    setErreur(null);
    setCherche(true);
    try {
      const r = await api.products.getAll({
        storeId: order.storeId,
        search: recherche,
        limit: 8,
      });
      setResultats(r.products || []);
    } catch (e: any) {
      setErreur(e?.message || "Recherche impossible");
    } finally {
      setCherche(false);
    }
  }

  async function lancer(envoyer: boolean) {
    setErreur(null);
    setTravail(true);
    if (!envoyer) setReponse(null);
    try {
      const corps: any = {
        sku: choisi?.sku || recherche.trim(),
        prixUnitaire: Number(prix),
        quantite: Number(quantite) || 1,
        unite: "MAD",
      };
      if (envoyer) corps.envoyer = true;
      const r = await api.orders.essaiAjoutGlovo(order.id, corps);
      setApercu(r?.apercu || null);
      if (envoyer) setReponse(r);
    } catch (e: any) {
      setErreur(e?.message || "Appel impossible");
    } finally {
      setTravail(false);
    }
  }

  const pretAEnvoyer =
    apercu && Number(prix) > 0 && (choisi?.sku || recherche.trim());

  return (
    <div className="mt-6 border-2 border-dashed border-amber-400 rounded-lg bg-amber-50">
      <button
        onClick={() => setOuvert(!ouvert)}
        className="w-full text-left px-4 py-3 font-semibold text-amber-900 flex items-center justify-between"
      >
        <span>Ajouter un article a cette commande — essai technique</span>
        <span>{ouvert ? "fermer" : "ouvrir"}</span>
      </button>

      {ouvert && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-amber-900 leading-relaxed">
            Reserve a l&apos;administration. Rien n&apos;est enregistre dans
            l&apos;application : cet encart sert uniquement a savoir si Glovo
            accepte l&apos;ajout, et dans quelle unite. Tant que tu ne cliques
            pas sur le bouton rouge, aucun appel ne part chez Glovo.
          </p>

          {/* --- 1. trouver l'article ----------------------------------- */}
          <div>
            <label className="block text-xs font-medium mb-1">
              1. Chercher un article du magasin
            </label>
            <div className="flex gap-2">
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") chercher();
                }}
                placeholder="nom du produit, ou SKU exact"
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
              <button
                onClick={chercher}
                disabled={cherche || !recherche.trim()}
                className="px-3 py-1 bg-gray-700 text-white rounded text-sm disabled:opacity-50"
              >
                {cherche ? "..." : "Chercher"}
              </button>
            </div>

            {resultats.length > 0 && (
              <div className="mt-2 max-h-48 overflow-auto border rounded bg-white divide-y">
                {resultats.map((p: any) => (
                  <button
                    key={p.id || p.sku}
                    onClick={() => {
                      setChoisi(p);
                      setResultats([]);
                    }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50"
                  >
                    <div className="font-medium">{p.name || p.nom}</div>
                    <div className="text-gray-500">
                      SKU {p.sku}
                      {/* Les champs de prix different d'un produit a l'autre.
                          On montre ce qu'on trouve, sans convertir : deviner
                          une unite ici produirait un prix faux. */}
                      {p.price !== undefined && ` — price: ${p.price}`}
                      {p.prixVente !== undefined && ` — prixVente: ${p.prixVente}`}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {choisi && (
              <div className="mt-2 text-xs bg-white border rounded px-2 py-1.5">
                Choisi : <strong>{choisi.name || choisi.nom}</strong> — SKU{" "}
                <code>{choisi.sku}</code>
              </div>
            )}
          </div>

          {/* --- 2. prix et quantite ------------------------------------ */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">
                2. Prix de vente en DH
              </label>
              <input
                type="number"
                step="0.01"
                value={prix}
                onChange={(e) => setPrix(e.target.value)}
                placeholder="ex. 20"
                className="w-full border rounded px-2 py-1 text-sm"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                A saisir a la main : les prix du catalogue ne sont pas tous
                stockes dans la meme unite, et un prix devine serait facture
                a la cliente.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Quantite</label>
              <input
                type="number"
                min="1"
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                Total actuel de la commande : <strong>{totalMad} DH</strong>
              </p>
            </div>
          </div>

          {/* --- 3. apercu ---------------------------------------------- */}
          <div className="flex gap-2">
            <button
              onClick={() => lancer(false)}
              disabled={travail || !Number(prix) || !(choisi?.sku || recherche.trim())}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
            >
              {travail ? "..." : "3. Voir l'apercu (rien n'est envoye)"}
            </button>
          </div>

          {apercu && (
            <div className="text-xs bg-white border rounded p-3 space-y-1">
              <div>
                Total actuel : <strong>{apercu.totalActuel} DH</strong>
              </div>
              <div>
                Article ajoute : <strong>+{apercu.ajout} DH</strong>
              </div>
              <div>
                Montant qui serait confirme a Glovo :{" "}
                <strong>{apercu.montantConfirme} DH</strong>
              </div>
              <div className="text-gray-500">
                SKU {apercu.article?.sku} — commande Glovo {apercu.glovoOrderId}
              </div>
            </div>
          )}

          {/* --- 4. envoi reel ------------------------------------------ */}
          {pretAEnvoyer && (
            <div className="border-t pt-3">
              <p className="text-xs text-red-800 mb-2">
                Le bouton ci-dessous appelle Glovo pour de vrai, sur cette
                commande. Verifie d&apos;abord que le montant confirme
                ci-dessus est le bon.
              </p>
              <button
                onClick={() => lancer(true)}
                disabled={travail}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-sm disabled:opacity-50"
              >
                {travail ? "..." : "4. Envoyer reellement a Glovo"}
              </button>
            </div>
          )}

          {reponse && (
            <div className="text-xs bg-gray-900 text-green-200 rounded p-3 overflow-auto max-h-64">
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(reponse, null, 2)}
              </pre>
            </div>
          )}

          {erreur && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {erreur}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
